import '@/commands/builtins'
import { createEffect, createMemo, createSignal, ErrorBoundary, For, onCleanup, onMount, Show, Suspense, untrack, } from 'solid-js'
import { createStore } from 'solid-js/store'
import { Dynamic } from 'solid-js/web'
import { vec2f, vec3f, vec4f } from 'typegpu/data'
import { clamp } from 'typegpu/std'
import { executeCommand } from '@/commands/registry'
import { useAuth } from '@/contexts/AuthContext'
import { useKeyframeTarget } from '@/contexts/KeyframeTargetContext'
import { useToast } from '@/contexts/ToastContext'
import { trackAppInit } from '@/lib/telemetry'
import { WheelZoomCamera2D } from '@/lib/WheelZoomCamera2D'
import { WheelZoomCamera3D } from '@/lib/WheelZoomCamera3D'
import { useShortcutManager } from '@/shortcuts'
import { createDragHandler } from '@/utils/createDragHandler'
import { recordEntries, recordKeys } from '@/utils/record'
import ui from './App.module.css'
import { AffineEditor } from './components/AffineEditor/AffineEditor'
import { AncestryTreeModal } from './components/AncestryTreeModal/AncestryTreeModal'
import { AudioReactivePanel } from './components/AudioReactivePanel/AudioReactivePanel'
import { createShowBenchmark } from './components/BenchmarkModal/BenchmarkModal'
import { BlendFlameGallery } from './components/BlendFlameGallery/BlendFlameGallery'
import { BreedGallery } from './components/BreedGallery/BreedGallery'
import { Button } from './components/Button/Button'
import { Checkbox } from './components/Checkbox/Checkbox'
import { CollapsibleCard } from './components/CollapsibleCard/CollapsibleCard'
import { ColorPicker } from './components/ColorPicker/ColorPicker'
import { Card } from './components/ControlCard/ControlCard'
import { ConfirmDeleteVariationModal } from './components/CustomVariationEditor/ConfirmDeleteVariationModal'
import { createShowCustomVariationEditor } from './components/CustomVariationEditor/CustomVariationEditor'
import { DebugOverlay } from './components/DebugOverlay'
import { DiceButton } from './components/DiceButton/DiceButton'
import { DiffViewContent, DiffViewModal, } from './components/DiffViewModal/DiffViewModal'
import diffUi from './components/DiffViewModal/DiffViewModal.module.css'
import { createDiscordShareModal } from './components/DiscordShareModal/DiscordShareModal'
import { createShowDocumentation } from './components/DocumentationModal/DocumentationModal'
import { Dropzone } from './components/Dropzone/Dropzone'
import { EvolutionChamber } from './components/EvolutionChamber/EvolutionChamber'
import { ExportActions } from './components/ExportJobs/ExportActions'
import { ExportJobHost } from './components/ExportJobs/ExportJobHost'
import { ExportJobTracker } from './components/ExportJobs/ExportJobTracker'
import { createExportPngDialog } from './components/ExportPngDialog/ExportPngDialog'
import { ColorEditor } from './components/FlameColorEditor/ColorEditor'
import { handleColor } from './components/FlameColorEditor/FlameColorEditor'
import { FlameRandomizerCard } from './components/FlameRandomizerCard/FlameRandomizerCard'
import { FloatingActions } from './components/FloatingActions/FloatingActions'
import { createShowHelp } from './components/HelpModal/HelpModal'
import { createImportVariationsModal } from './components/ImportVariationsModal/ImportVariationsModal'
import { ConfirmOverwriteRecentModal } from './components/LoadFlameModal/ConfirmOverwriteRecentModal'
import { createLoadFlame } from './components/LoadFlameModal/LoadFlameModal'
import { createLogoFaviconGenerator } from './components/LogoFaviconGenerator/LogoFaviconGenerator'
import { createMigrationModal } from './components/Migration/Migration'
import { useRequestModal } from './components/Modal/ModalContext'
import { OrientationGizmo } from './components/OrientationGizmo/OrientationGizmo'
import { PaletteSelector } from './components/PaletteSelector/PaletteSelector'
import { PopulationSimulator } from './components/PopulationSimulator/PopulationSimulator'
import { ProgressBar } from './components/ProgressBar/ProgressBar'
import { getPresetFromQuality, qualityPresets, } from './components/Quality/QualityPresets'
import { QuickVariationPicker } from './components/QuickVariationPicker/QuickVariationPicker'
import { createServerRenderDialog } from './components/ServerRenderDialog/ServerRenderDialog'
import { createShareLinkModal } from './components/ShareLinkModal/ShareLinkModal'
import { createShareVariationLinkModal, createShareVariationLoadModal, } from './components/ShareVariationModal/ShareVariationModal'
import { AngleEditor } from './components/Sliders/ParametricEditors/AngleEditor'
import { ScrubInput } from './components/Sliders/ScrubInput'
import { Slider } from './components/Sliders/Slider'
import { SoftwareVersion } from './components/SoftwareVersion/SoftwareVersion'
import { SonificationPanel } from './components/SonificationPanel/SonificationPanel'
import { SpotlightTour } from './components/SpotlightTour/SpotlightTour'
import { KeyframeDiamond } from './components/Timeline/KeyframeDiamond'
import { smartRandomAnimation } from './components/Timeline/presets'
import { TimelineSection } from './components/Timeline/TimelineSection'
import { createVariationSelector } from './components/VariationSelector/VariationSelector'
import { ViewControls } from './components/ViewControls/ViewControls'
import { ChangeHistoryContextProvider } from './contexts/ChangeHistoryContext'
import { useCompactMode } from './contexts/CompactModeContext'
import { useTheme } from './contexts/ThemeContext'
import { TimelineContextProvider } from './contexts/TimelineContext'
import { DEFAULT_POINT_COUNT, DEFAULT_QUALITY, DEFAULT_RENDER_INTERVAL_MS, DEFAULT_RESOLUTION, IS_DEV, } from './defaults'
import { colorInitModeToImplFn } from './flame/colorInitMode'
import { applyColorMapToFlame } from './flame/colorMap'
import { drawModeToImplFn } from './flame/drawMode'
import { example1 } from './flame/examples/example1'
import { example34 } from './flame/examples/example34'
import { initExample } from './flame/examples/initExample'
import { initExample3D } from './flame/examples/initExample3D'
import { tid as toTransformId, vid as toVariationId, } from './flame/examples/util'
import { Flam3 } from './flame/Flam3'
import { pointInitModeToImplFn } from './flame/pointInitMode'
import { pointInitMode3DToImplFn } from './flame/pointInitMode3D'
import { generateRandomFlame, mutateFlame, random01, randomizeAllColors, randomizeVariationParams, randomRange, } from './flame/randomize'
import { accumulatedPointCount, animationExportBackend, animationExportCancel, animationExportProgress, animationExportRunning, cameraDuringExportEnabled, exportQuality, qualityPointCountLimit, setAnimationExportBackend, setAnimationExportCancel, setAnimationExportRunning, setCurrentQuality, setExportQuality, setForceAnimationExportNow, setQualityPointCountLimit, } from './flame/renderStats'
import { MAX_CAMERA_ZOOM_VALUE, MIN_CAMERA_ZOOM_VALUE, tryValidateFlame, } from './flame/schema/flameSchema'
import { generateTransformId, generateVariationId, } from './flame/transformFunction'
import { defaultLinearType } from './flame/variationRegistry'
import { allTransformVariations, isAnyParametricVariationType, isVariationType, } from './flame/variations'
import { deleteCustomVariation, duplicateCustomVariation, getCustomVariations, isCustomVariationRegistered, loadCustomVariations, persistSharedVariations, restoreCustomVariation, } from './flame/variations/custom'
import { getNormalizedVariationName, getParamsEditor, getVariationDefault, } from './flame/variations/utils'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { BoxArrowRight, Cross, Eye, EyeOff, Menu, Plus, Share, Shuffle, Terminal, } from './icons'
import { AutoCanvas } from './lib/AutoCanvas'
import { createAnimationExport } from './utils/animationExport'
import { createAudioAnalyzer } from './utils/audioAnalysis'
import { autosaveIntervalMin, autosaveRecents, saveReminderDismissed, setAutosaveRecents, setSaveReminderDismissed, } from './utils/autosaveSettings'
import { downloadBlob } from './utils/blob'
import { deepClone } from './utils/clone'
import { createStoreHistory } from './utils/createStoreHistory'
import { sendFlameToDiscord } from './utils/discordWebhook'
import { enqueueAnimationJob, enqueueImageJob } from './utils/exportJobs'
import { addFlameDataToPng } from './utils/flameInPng'
import { hardwareTierToPreset } from './utils/hardwareTier'
import { compressJsonQueryParam } from './utils/jsonQueryParam'
import { persistentSignal } from './utils/persistentSignal'
import { addRandomizerHistoryEntry, clearRandomizerHistory, loadRandomizerHistoryEntries, MAX_RANDOMIZER_HISTORY_LIMIT, } from './utils/randomizerHistoryDB'
import { buildReadableIds } from './utils/readableIds'
import { getOldestRecentFlame, saveRecentFlame, upsertRecentFlame, } from './utils/recentFlames'
import { createShareLink, deriveOgMeta, uploadOgPreview, } from './utils/shareLink'
import { sum } from './utils/sum'
import { createTimelineState, resolveKeyframeValue } from './utils/timeline'
import { sortedTransformEntries } from './utils/transformOrder'
import { createUndoRouter } from './utils/undoRouting'
import { useAppDragAndDrop } from './utils/useAppDragAndDrop'
import { useAudioReactive } from './utils/useAudioReactive'
import { useKeyboardShortcuts } from './utils/useKeyboardShortcuts'
import { useSonification } from './utils/useSonification'
import type { Setter } from 'solid-js'
import type { v2f } from 'typegpu/data'
import type { Vec3 } from 'wgpu-matrix'
import type { AudioMapping } from './components/AudioReactivePanel/AudioReactivePanel'
import type { QualityPreset } from './components/Quality/QualityPresets'
import type { QuickPickerMode } from './components/QuickVariationPicker/QuickVariationPicker'
import type { TourContext } from './components/SpotlightTour/tourTypes'
import type { ColorMap, Palette } from './flame/colorMap'
import type { PointInitMode } from './flame/pointInitMode'
import type { GenerateRandomFlameConfig, MutateFlameOptions, } from './flame/randomize'
import type { FlameDescriptor, TransformFunction, TransformId, VariationId, } from './flame/schema/flameSchema'
import type { Dims } from './flame/variationRegistry'
import type { TransformVariationType } from './flame/variations'
import type { CustomVariationDef } from './flame/variations/custom/types'
import type { TransformVariationType3D } from './flame/variations3D'
import type { AnimationExportConfig } from './utils/animationExport'
import type { AudioAnalyzer, LiveAudioAnalyzer } from './utils/audioAnalysis'
import type { ExportDimensions } from './utils/exportDimensions'
import type { HardwareTier } from './utils/hardwareTier'
import type { SharePayload } from './utils/jsonQueryParam'
import type { RandomizerHistoryEntry } from './utils/randomizerHistoryDB'
import type { SonificationConfig } from './utils/sonification'
import type { EasingCurve, TimelineTrack } from './utils/timeline'
import type { CommandContext } from '@/commands/types'

const EDGE_FADE_COLOR = {
  light: vec4f(0.96, 0.96, 0.96, 0.7),
  dark: vec4f(0, 0, 0, 0.6),
}

function formatPercent(x: number) {
  if (x === 1) {
    return `100 %`
  }
  return `${(x * 100).toFixed(1)} %`
}

function newDefaultTransform(dims: Dims = 2): TransformFunction {
  const is3D = dims === 3
  const identity = is3D
    ? {
        a: 1,
        b: 0,
        c: 0,
        d: 0,
        e: 0,
        f: 1,
        g: 0,
        h: 0,
        i: 0,
        j: 0,
        k: 1,
        l: 0,
      }
    : { a: 1, b: 0, c: 0, d: 0, e: 1, f: 0 }
  return {
    probability: 1,
    colorSpeed: 0.4,
    color: { x: 0, y: 0 },
    preAffine: identity,
    postAffine: identity,
    visible: true,
    variations: {
      [generateVariationId()]: getVariationDefault(
        defaultLinearType(dims),
        1.0,
      ),
    },
  }
}

export type ExportImageInfo = {
  /** True when the canvas holds a final color-graded image at the requested
   *  quality limit, i.e. it is safe to capture the canvas for an export. */
  finalImageReady: boolean
}

export type ExportImageType = (
  canvas: HTMLCanvasElement,
  info?: ExportImageInfo,
) => void

export type AppProps = {
  /**
   * Decoded shared payload. `importedCustomVariations` /
   * `alreadyOwnedCustomVariations` are runtime-only (set by the share-load path
   * in App.tsx, never serialized): respectively the custom variations
   * re-validated and registered transiently (offered to save via the consent
   * prompt), and the ones whose code already matches the user's saved library.
   */
  flameFromQuery?: SharePayload & {
    importedCustomVariations?: CustomVariationDef[]
    alreadyOwnedCustomVariations?: CustomVariationDef[]
  }
  /**
   * A single custom variation shared via a `?cv=` link, already re-validated and
   * transiently registered by App.tsx. `alreadyOwned` is true when the code
   * matches one already in the user's library. Runtime-only (never serialized).
   */
  sharedVariationFromQuery?: {
    def: CustomVariationDef
    alreadyOwned: boolean
  }
  flameFromWelcome?: () => FlameDescriptor | undefined
  welcomeTracks?: () => TimelineTrack[] | undefined
  resetFlameFromWelcome?: () => void
  hardwareTier?: HardwareTier | null
  onHardwareTierChange?: (tier: HardwareTier) => void
  /** When true (driven by the `?benchmark` query param), open the benchmark
   *  dialog on mount so the user lands one click from running it. */
  autoOpenBenchmark?: boolean
  /** When true (`?benchmark=auto`), also start the run automatically. */
  autoStartBenchmark?: boolean
}

export function extractFlameVariationTypes(
  descriptor: FlameDescriptor,
): TransformVariationType[] {
  const result: TransformVariationType[] = []
  for (const transform of Object.values(descriptor.transforms)) {
    for (const variation of Object.values(transform.variations)) {
      result.push(variation.type)
    }
  }
  return result
}

function addTransformWithVariation(draft: FlameDescriptor, type: string) {
  const t = deepClone(
    newDefaultTransform((draft.renderSettings.dimensions ?? 2) as Dims),
  )
  t.variations = {
    [generateVariationId()]: {
      type,
      weight: 1,
      visible: true,
    },
  }
  draft.transforms[generateTransformId()] = t
}

export function MainWorkspace(props: AppProps) {
  const { theme, setTheme } = useTheme()
  const { targetedParameter, setTargetedParameter } = useKeyframeTarget()
  const auth = useAuth()
  let isRandomizingAnimation = false

  createEffect(() => {
    const path = targetedParameter()
    if (path && !isRandomizingAnimation) {
      // Find the element with the matching data-parameter-path
      const el = document.querySelector(`[data-parameter-path="${path}"]`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
  })

  const [qualityPreset, setQualityPreset] = createSignal<QualityPreset>(
    props.hardwareTier
      ? hardwareTierToPreset(props.hardwareTier)
      : getPresetFromQuality(DEFAULT_QUALITY),
  )

  createEffect(() => {
    if (props.hardwareTier) {
      setQualityPreset(hardwareTierToPreset(props.hardwareTier))
    }
  })

  const [pixelRatio, setPixelRatio] = createSignal(DEFAULT_RESOLUTION)
  // When set during an export, the main canvas renders at this exact pixel size
  // (resolution + aspect resolved) instead of the viewport-scaled pixelRatio, so
  // the captured image/video matches the chosen export format.
  const [exportDimensions, setExportDimensions] = createSignal<
    ExportDimensions | undefined
  >()
  // Hoist this conditional out of the AutoCanvas JSX prop: a ternary in a prop
  // compiles to a memo that Solid instantiates lazily on first read — and the
  // first read happens inside Flam3's rAF export loop (no owner), which warns
  // "computations created outside a createRoot". Created here it lives in this
  // component's owner. See memory: solid-conditional-prop-memo-leak.
  const canvasPixelRatio = createMemo(() =>
    exportDimensions() ? 1 : pixelRatio(),
  )
  const [onExportImage, setOnExportImage] = createSignal<ExportImageType>()

  // Dev-only: crash injection trigger (renders inside ErrorBoundary)
  const [devCrashTest, setDevCrashTest] = createSignal(false)
  const [adaptiveFilterEnabled, setAdaptiveFilterEnabled] = createSignal(true)
  const [stochasticFilterEnabled, setStochasticFilterEnabled] =
    createSignal(false)
  // Which transform is "selected" — shared across the affine grid, the color
  // picker and the sidebar transform cards so it's clear which one edits target.
  const [selectedTransformId, setSelectedTransformId] = createSignal<
    string | null
  >(null)
  // Toggle: clicking the already-selected transform clears the selection
  // (deselect-all → nothing dimmed). Canvas handles only ever *set* (drag-safe).
  const toggleSelectedTransform = (tid: string) =>
    setSelectedTransformId((prev) => (prev === tid ? null : tid))
  // Per-transform collapsed state drives the (controlled) transform cards, so the
  // sidebar toolbar toggle can collapse-all / expand-all based on actual state:
  // if any card is open it collapses all, otherwise it expands all.
  const [collapsedTransforms, setCollapsedTransforms] = createSignal<
    Set<string>
  >(new Set())
  const visibleTransformTids = () =>
    sortedTransformEntries(recordEntries(flameDescriptor.transforms))
      .filter(([tid]) => !tid.startsWith('_sym__'))
      .map(([tid]) => tid)
  const anyTransformOpen = () =>
    visibleTransformTids().some((tid) => !collapsedTransforms().has(tid))

  function toggleCollapseAllTransforms() {
    setCollapsedTransforms(
      anyTransformOpen() ? new Set(visibleTransformTids()) : new Set<string>(),
    )
  }

  function toggleTransformCollapsed(tid: string) {
    setCollapsedTransforms((prev) => {
      const next = new Set(prev)
      if (next.has(tid)) next.delete(tid)
      else next.add(tid)
      return next
    })
  }

  // Browser tab title: "Lumen Apeiron — <flame name>" when the flame is named,
  // otherwise just "Lumen Apeiron".
  createEffect(() => {
    const name = flameDescriptor.metadata?.name?.trim()

    document.title =
      name && name.toLowerCase() !== 'unknown'
        ? `Lumen Apeiron — ${name}`
        : 'Lumen Apeiron'
  })

  const [animationEnabled, setAnimationEnabled] = createSignal(true)
  const [hideDiceButtons, setHideDiceButtons] = createSignal(false)
  // True while a randomize/mutate run is in flight, so the buttons disable and
  // rapid clicks can't pile up concurrent runs (history thumbnail capture).
  const [isRandomizing, setIsRandomizing] = createSignal(false)
  const { toastMessage, showToast } = useToast()
  const SIDEBAR_RESIZABLE = false
  const { isCompact, setCompact } = useCompactMode()
  const [showSidebar, setShowSidebar] = createSignal(true)
  const [sidebarDiffView, setSidebarDiffView] = createSignal<{
    flameA: FlameDescriptor
    flameB: FlameDescriptor
  } | null>(null)
  const _requestModal = useRequestModal()
  const [sidebarHidden, setSidebarHidden] = createSignal(
    window.innerWidth < 769,
  )
  // Flame Randomizer card open state is controlled here so the Timeline
  // "Animate" button can reveal it; the epoch bump also forces its Animation
  // Settings section open.
  const [randomizerOpen, setRandomizerOpen] = createSignal(false)
  const [randomizerAnimEpoch, setRandomizerAnimEpoch] = createSignal(0)
  const [sidebarLayoutMode, setSidebarLayoutMode] = persistentSignal<
    'compact' | 'wide'
  >('sidebar-layout-mode', 'wide')
  const sidebarWidth = createMemo(() =>
    sidebarLayoutMode() === 'wide' ? 26 : 21,
  )
  const setSidebarWidth = () => {} // Drag resize disabled
  let sidebarRef: HTMLDivElement | undefined
  let sidebarScrollRef: HTMLDivElement | undefined
  let randomizerCardRef: HTMLDivElement | undefined
  let savedScrollTop = 0
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sidebarEl, setSidebarEl] = createSignal<HTMLDivElement | undefined>()
  const floatingLeft = () => {
    const rootFontSize = parseFloat(
      // eslint-disable-next-line no-restricted-globals
      getComputedStyle(document.documentElement).fontSize,
    )
    return sidebarWidth() * rootFontSize + 8
  }
  const floatingTop = () => 8
  const [isMobile, setIsMobile] = createSignal(window.innerWidth < 769)
  createEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    setIsMobile(mq.matches)
    if (mq.matches) setCompact(true)
    const handler = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches)
      if (e.matches) setCompact(true)
      // Auto-hide sidebar when switching to mobile layout
      if (e.matches) setSidebarHidden(true)
    }
    mq.addEventListener('change', handler)
    return () => {
      mq.removeEventListener('change', handler)
    }
  })
  // Hide timeline by default on mobile -- users can toggle it back on
  const [showTimeline, setShowTimeline] = createSignal(window.innerWidth >= 769)
  // Colors as they were before the first palette apply — lets Unselect
  // restore the "natural" colors. UI stash only; undo handles the rest.
  const [prePaletteColors, setPrePaletteColors] = createSignal<
    Record<string, { x: number; y: number }>
  >({})
  const [flameDescriptor, setFlameDescriptor, history] = createStoreHistory(
    createStore(
      deepClone(
        props.flameFromWelcome?.() ?? props.flameFromQuery?.flame ?? example1,
      ),
    ),
    // The main flame history joins the app-wide undo journal so Ctrl+Z can
    // arbitrate chronologically against the timeline's undo stack.
    { journal: true },
  )
  // Palette selection is part of the flame document (renderSettings.palette):
  // applying/removing one is a single undoable history entry, and the palette
  // travels with saves/shares. These accessors derive the UI/render views.
  const selectedPalette = createMemo<Palette | undefined>(() => {
    const stored = flameDescriptor.renderSettings.palette
    if (!stored) return undefined
    return {
      id: stored.id,
      name: stored.name,
      entries: stored.entries.map((entry) => ({ ...entry })),
      source: 'imported',
    }
  })
  const selectedPaletteId = () =>
    flameDescriptor.renderSettings.palette?.id ?? ''
  // Blend composition is part of the flame document too (renderSettings
  // .blendFlame / .blendWeight): picking, adjusting, or clearing a blend is
  // one undoable history entry each, and the composition survives
  // save/share/load. The stored blend flame is plain data, re-validated on
  // read so a hand-edited file can't hand the renderer an invalid flame.
  const blendFlame = createMemo<FlameDescriptor | undefined>(() => {
    const stored = flameDescriptor.renderSettings.blendFlame
    if (stored === undefined) return undefined
    return tryValidateFlame(stored)
  })
  const blendWeight = () => flameDescriptor.renderSettings.blendWeight ?? 0
  const setBlendFlame = (flame: FlameDescriptor | undefined) => {
    setFlameDescriptor(
      (draft) => {
        if (flame === undefined) delete draft.renderSettings.blendFlame
        else draft.renderSettings.blendFlame = deepClone(flame)
      },
      flame ? 'Set Blend Flame' : 'Remove Blend Flame',
    )
  }
  const setBlendWeight = (weight: number) => {
    setFlameDescriptor((draft) => {
      draft.renderSettings.blendWeight = weight
    }, 'Blend Weight')
  }
  if (IS_DEV) {
    console.info('[share:app] store initialized', {
      source: props.flameFromWelcome?.()
        ? 'welcome'
        : props.flameFromQuery?.flame
          ? 'query'
          : 'default',
      transformCount: recordKeys(flameDescriptor.transforms).length,
      firstColor: Object.values(flameDescriptor.transforms)[0]?.color,
      queryFlamePresent: !!props.flameFromQuery?.flame,
      queryAnimPresent: !!props.flameFromQuery?.animation,
    })
  }
  createEffect(() => {
    const newFlame = props.flameFromWelcome?.()
    if (newFlame !== undefined) {
      flushDirtyToRecents()
      history.replace(deepClone(newFlame))
      // Load animation tracks if the welcome selection includes them
      const tracks = props.welcomeTracks?.()
      if (IS_DEV) {
        console.info('[welcome] flame selected, tracks:', {
          hasTracks: !!tracks,
          trackCount: tracks?.length ?? 0,
          trackPaths: tracks?.map((t) => t.parameterPath) ?? [],
        })
      }
      if (tracks && tracks.length > 0) {
        setLoadedAnimation({
          flame: deepClone(newFlame),
          tracks: tracks.map((t) => ({
            ...t,
            keyframes: t.keyframes.map((kf) => ({ ...kf })),
          })),
        })
      }
      props.resetFlameFromWelcome?.()
      // A welcome pick is a fresh starting point for dirty tracking.
      markLoadedBaseline()
    }
  })

  const symTransforms = createMemo(() =>
    sortedTransformEntries(recordEntries(flameDescriptor.transforms)).filter(
      ([tid]) => tid.startsWith('_sym__'),
    ),
  )

  // Stable ID list for <For> -- only changes when transforms are added/removed,
  // not when their values change, so dragging angle editors stays fluid.
  const symTransformIds = createMemo(() => symTransforms().map(([tid]) => tid))

  const currentSymType = createMemo(() => {
    const syms = symTransforms() || []
    return syms.some(
      ([, t]) =>
        t?.preAffine?.a === -1 &&
        t.preAffine.d === 0 &&
        t.preAffine.b === 0 &&
        t.preAffine.e === 1,
    )
      ? 'dihedral'
      : 'rotational'
  })

  const currentSymFolds = createMemo(() => {
    const isDihedral = currentSymType() === 'dihedral'
    const syms = symTransforms() || []
    return isDihedral ? syms.length : syms.length + 1
  })

  const applySymmetry = (n: number, type: 'rotational' | 'dihedral') => {
    setFlameDescriptor((draft) => {
      for (const tid of recordKeys(draft.transforms)) {
        if (tid.startsWith('_sym__')) {
          delete draft.transforms[tid]
        }
      }

      const totalWeight = sum(
        Object.values(draft.transforms).map((t) => t.probability),
      )
      const symWeight = Math.max(totalWeight, 1)

      for (let i = 1; i < n; i++) {
        const angle = (2 * Math.PI * i) / n
        const cos = Math.cos(angle)
        const sin = Math.sin(angle)
        draft.transforms[generateTransformId('sym')] = {
          probability: symWeight,
          colorSpeed: 0,
          color: { x: 0, y: 0 },
          visible: true,
          preAffine: { a: cos, b: -sin, c: 0, d: sin, e: cos, f: 0 },
          postAffine: { a: 1, b: 0, c: 0, d: 0, e: 1, f: 0 },
          variations: {
            [generateVariationId()]: getVariationDefault(
              defaultLinearType(
                (flameDescriptor.renderSettings.dimensions ?? 2) as Dims,
              ),
              1,
            ),
          },
        }
      }

      if (type === 'dihedral') {
        draft.transforms[generateTransformId('sym')] = {
          probability: symWeight,
          colorSpeed: 0,
          color: { x: 0, y: 0 },
          visible: true,
          preAffine: { a: -1, b: 0, c: 0, d: 0, e: 1, f: 0 },
          postAffine: { a: 1, b: 0, c: 0, d: 0, e: 1, f: 0 },
          variations: {
            [generateVariationId()]: getVariationDefault(
              defaultLinearType(
                (flameDescriptor.renderSettings.dimensions ?? 2) as Dims,
              ),
              1,
            ),
          },
        }
      }
    })
  }

  const totalProbability = createMemo(() =>
    sum(Object.values(flameDescriptor.transforms).map((f) => f.probability)),
  )
  const {
    loadModalIsOpen,
    showLoadFlameModal,
    loadedAnimation,
    setLoadedAnimation,
    clearLoadedAnimation,
  } = createLoadFlame(
    history,
    () => flameDescriptor.renderSettings.dimensions ?? 2,
  )

  const [showBlendGallery, setShowBlendGallery] = createSignal(false)
  // Whether the blend-flame gallery is being used to set a static blend or to
  // set up a morph animation (animated blendWeight). Branches the gallery's
  // onSelect handler.
  const [blendIntent, setBlendIntent] = createSignal<
    'blend' | 'morph' | 'breed' | 'evolve' | 'diff'
  >('blend')

  // Audio-reactive panel state
  const [showAudioPanel, setShowAudioPanel] = createSignal(false)
  const [audioBuffer, setAudioBuffer] = createSignal<AudioBuffer | undefined>(
    undefined,
  )
  const [audioEnabled, setAudioEnabled] = createSignal(false)
  const [audioMapping, setAudioMapping] = createSignal<AudioMapping>({
    preset: 'pulse',
    mappings: [
      {
        audioFeature: 'bass',
        target: { kind: 'renderSetting', param: 'vibrancy' },
        sensitivity: 1,
        range: [0.3, 1.5],
      },
      {
        audioFeature: 'beat',
        target: { kind: 'renderSetting', param: 'palettePhase' },
        sensitivity: 1,
        range: [0, 3.14],
      },
    ],
  })
  const [audioSource, setAudioSource] = createSignal<'file' | 'mic'>('file')
  const [liveAnalyzer, setLiveAnalyzer] = createSignal<
    LiveAudioAnalyzer | undefined
  >(undefined)
  const [playbackPaused, setPlaybackPaused] = createSignal(false)
  const [seekTarget, setSeekTarget] = createSignal<number | null>(null)
  const [playbackTime, setPlaybackTime] = createSignal(0)
  const [fileAnalyzer, setFileAnalyzer] = createSignal<
    AudioAnalyzer | undefined
  >(undefined)

  // Reset playback state when switching between file and mic
  createEffect(() => {
    const _src = audioSource()
    setPlaybackPaused(false)
    setPlaybackTime(0)
    setSeekTarget(null)
  })

  // Derive transform list for audio mapping target selectors
  const transformInfos = createMemo(() => {
    const txs = flameDescriptor.transforms
    return Object.entries(txs).map(([id, tx], i) => {
      const variations = Object.entries(tx.variations ?? {}).map(
        ([vid, v]) => ({
          id: vid,
          type: (v as { type: string }).type,
        }),
      )
      return {
        id,
        index: i,
        label: `Tx ${i}: ${id.split('_')[0]?.slice(0, 12) ?? id.slice(0, 12)}`,
        variations,
      }
    })
  })

  // Sonification state
  const [showSonificationPanel, setShowSonificationPanel] = createSignal(false)
  const [sonificationEnabled, setSonificationEnabled] = createSignal(false)
  const [sonificationConfig, setSonificationConfig] =
    createSignal<SonificationConfig>({
      model: 'orchestral',
      volume: 0.3,
      updateRate: 20,
      scale: 'pentatonicMajor',
      voiceCount: 8,
      harmonicDensity: 1.0,
      triggerRate: 4,
      spatialSpread: 0.7,
      reverbMix: 0.3,
    })

  function pickBlendFlame() {
    setBlendIntent('blend')
    setShowAudioPanel(false)
    setShowSonificationPanel(false)
    setShowSidebar(true)
    setShowBlendGallery(true)
  }

  function pickMorphFlame() {
    setBlendIntent('morph')
    setShowAudioPanel(false)
    setShowSonificationPanel(false)
    setShowSidebar(true)
    setShowBlendGallery(true)
  }

  function pickBreedFlame() {
    setBlendIntent('breed')
    setShowSidebar(true)
    setShowBlendGallery(true)
  }

  function pickEvolveFlame() {
    setBlendIntent('evolve')
    setShowSidebar(true)
    setShowBlendGallery(true)
  }

  function pickDiffFlame() {
    setBlendIntent('diff')
    setShowSidebar(true)
    setShowBlendGallery(true)
  }

  function openDiffView(flameA: FlameDescriptor, flameB: FlameDescriptor) {
    setSidebarDiffView({ flameA: deepClone(flameA), flameB: deepClone(flameB) })
    setShowSidebar(true)
  }

  /** Opens DiffViewModal on top of the current modal stack — used when
   *  compare/diff is triggered from within an already-open modal so the
   *  diff doesn't render behind the ::backdrop. */
  function openDiffAsModal(flameA: FlameDescriptor, flameB: FlameDescriptor) {
    void _requestModal({
      content: ({ respond }) => (
        <DiffViewModal
          flameA={deepClone(flameA)}
          flameB={deepClone(flameB)}
          respond={respond}
        />
      ),
    })
  }

  /** Close the sidebar diff panel and return to editor view. */
  function closeSidebarDiff() {
    setSidebarDiffView(null)
  }

  function pickGalleryFlame() {
    // The gallery is a mode of the Load Flame dialog: same tiles and chrome,
    // plus search, variation tags, and the Bred & Evolved section.
    void showLoadFlameModal('gallery')
  }

  function pickSimulatorFlame() {
    void _requestModal({
      content: ({ respond }) => (
        <PopulationSimulator
          flame={flameDescriptor}
          hardwareTier={props.hardwareTier}
          onApply={(flame) => {
            if (blendFlame())
              showToast(
                'Blend is still active — the loaded flame will look mixed',
                4000,
              )
            history.replace(deepClone(flame))
          }}
          respond={respond}
        />
      ),
    })
  }

  function pickAncestryFlame() {
    void _requestModal({
      content: ({ respond }) => (
        <AncestryTreeModal
          flame={flameDescriptor}
          hardwareTier={props.hardwareTier}
          onApply={(flame) => {
            if (blendFlame())
              showToast(
                'Blend is still active — the loaded flame will look mixed',
                4000,
              )
            history.replace(deepClone(flame))
          }}
          onCompare={openDiffAsModal}
          respond={respond}
        />
      ),
    })
  }

  /**
   * Set up an animated morph from the current flame (A) into `endFlame` (B).
   * Reuses the Blend pipeline: B becomes the blend flame and `blendWeight` is
   * keyframed 1 (pure A) → 0 (pure B) across the timeline, so playback
   * cross-dissolves A into B. Combine with "Seamless Loop" for an A→B→A cycle.
   */
  function setupMorph(endFlame: FlameDescriptor) {
    // One flame-history entry for the composition (blend flame + weight)...
    setFlameDescriptor((draft) => {
      draft.renderSettings.blendFlame = deepClone(endFlame)
      draft.renderSettings.blendWeight = 1
    }, 'Morph Setup')
    const cfg = timeline.config()
    // ...and one timeline undo step for the keyframes (remove + both adds).
    timeline.runWithSingleUndo(() => {
      timeline.removeAllKeyframesForPath('blendWeight')
      timeline.addKeyframe('blendWeight', cfg.startFrame, 1, 'easeInOut')
      timeline.setKeyframeValue('blendWeight', cfg.endFrame, 0, 'easeInOut')
    })
    setAnimationEnabled(true)
    setShowTimeline(true)
    timeline.goToFrame(cfg.startFrame)
    showToast('Morph ready — press Play to animate A → B', 3500)
  }

  // Hover preview: temporarily set blend flame at 40% weight. Silent writes —
  // a transient hover must not create history entries or clobber redo.
  let prevBlendFlame: FlameDescriptor | undefined
  let prevBlendWeight = 0
  let blendPreviewActive = false

  function handlePreviewBlend(flame: FlameDescriptor | null) {
    if (flame) {
      if (!blendPreviewActive) {
        prevBlendFlame = blendFlame()
        prevBlendWeight = blendWeight()
        blendPreviewActive = true
      }
      history.setSilently((draft) => {
        draft.renderSettings.blendFlame = deepClone(flame)
        draft.renderSettings.blendWeight = 0.4
      })
    } else if (blendPreviewActive) {
      const restore = prevBlendFlame
      const restoreWeight = prevBlendWeight
      history.setSilently((draft) => {
        if (restore === undefined) delete draft.renderSettings.blendFlame
        else draft.renderSettings.blendFlame = deepClone(restore)
        draft.renderSettings.blendWeight = restoreWeight
      })
      prevBlendFlame = undefined
      blendPreviewActive = false
    }
  }

  const [hoveredBlendName, setHoveredBlendName] = createSignal<string | null>(
    null,
  )

  const { showVariationSelector, varSelectorModalIsOpen } =
    createVariationSelector(history, props.hardwareTier)

  const { showCustomVariationEditor, customVariationEditorIsOpen } =
    createShowCustomVariationEditor()

  const isAnyModalOpen = () =>
    loadModalIsOpen() ||
    varSelectorModalIsOpen() ||
    exportModalIsOpen() ||
    customVariationEditorIsOpen()

  // Quick variation picker state
  const [quickPickerMode, setQuickPickerMode] =
    persistentSignal<QuickPickerMode>('quick-picker-mode', 'list')
  type QuickPickState = {
    tid: TransformId
    vid: VariationId
    type: TransformVariationType | TransformVariationType3D
  } | null
  const [quickPickState, setQuickPickState] = createSignal<QuickPickState>(null)
  const [hoveredVariationType, setHoveredVariationType] = createSignal<
    TransformVariationType | TransformVariationType3D | null
  >(null)
  const [hoveredCustomVarDef, setHoveredCustomVarDef] =
    createSignal<CustomVariationDef | null>(null)

  // Trigger for refreshing the custom variations list (incremented on delete/duplicate/modal close)
  const [customVarsVersion, setCustomVarsVersion] = createSignal(0)
  const customVariationsList = createMemo(() => {
    void customVarsVersion()
    return getCustomVariations()
  })

  // Status of a variation type for the per-transform list badge: 'none' for
  // built-ins, 'available' for a live custom variation, 'unavailable' for one a
  // flame still references after it was deleted from the library (or never
  // imported). Reads customVarsVersion so the badge re-evaluates on delete/import.
  function customStatus(type: string): 'none' | 'available' | 'unavailable' {
    if (!type.startsWith('custom_')) return 'none'
    void customVarsVersion()
    return isCustomVariationRegistered(type) ? 'available' : 'unavailable'
  }

  // Close the quick variation picker when its target transform/variation no
  // longer exists in the current flame — i.e. the flame was switched or toggled
  // 2D<->3D (both replace the flame with different transforms). The picker's ids
  // are meaningless for another flame; previewing a stale id produced NaN affine
  // matrices (the SVG `<g>` transform error) and invalid WebGPU textures.
  createEffect(() => {
    const state = quickPickState()
    if (
      state !== null &&
      flameDescriptor.transforms[state.tid]?.variations[state.vid] === undefined
    ) {
      setQuickPickState(null)
    }
  })

  // Compute a temporary flame with the hovered variation swapped in.
  // Falls back to the real flameDescriptor when nothing is hovered.
  const effectiveFlame = createMemo<FlameDescriptor>(() => {
    // Custom variation hover — add a new transform on top
    const hoveredCV = hoveredCustomVarDef()
    if (hoveredCV) {
      try {
        const clone: FlameDescriptor = deepClone(flameDescriptor)
        const transform = newDefaultTransform()
        transform.variations = {
          [generateVariationId()]: {
            type: hoveredCV.id,
            weight: 1,
            visible: true,
          },
        }
        clone.transforms[generateTransformId()] = transform
        return clone
      } catch {
        return flameDescriptor
      }
    }

    const hovered = hoveredVariationType()
    const state = quickPickState()
    if (!hovered || !state) return flameDescriptor
    try {
      const clone: FlameDescriptor = deepClone(flameDescriptor)
      const existingVar = clone.transforms[state.tid]?.variations[state.vid]
      if (existingVar) {
        clone.transforms[state.tid]!.variations[state.vid] = deepClone(
          getVariationDefault(hovered, existingVar.weight),
        )
      }
      return clone
    } catch {
      return flameDescriptor
    }
  })

  const finalRenderInterval = () =>
    isAnyModalOpen()
      ? Infinity
      : onExportImage()
        ? 0
        : DEFAULT_RENDER_INTERVAL_MS

  const resolvedBlendWeight = createMemo(() => {
    if (
      blendFlame() &&
      timeline.animationEnabled() &&
      timeline.tracks().length > 0
    ) {
      const val = timeline.resolveValueAtPath(
        'blendWeight',
        timeline.currentFrame(),
      )
      if (val !== null && typeof val === 'number') return val
    }
    return blendWeight()
  })

  const handlePaletteSelect = (palette: Palette) => {
    // If no palette was selected before, save the current "natural" colors
    if (selectedPaletteId() === '') {
      const colors: Record<string, { x: number; y: number }> = {}
      for (const [tid, t] of recordEntries(flameDescriptor.transforms)) {
        colors[tid] = { x: t.color.x, y: t.color.y }
      }
      setPrePaletteColors(colors)
    }

    // Convert palette entries to color map entries and apply
    const entries = palette.entries.map((entry) => ({ a: entry.a, b: entry.b }))
    const colorMap: ColorMap = {
      id: palette.id,
      name: palette.name,
      entries,
    }
    // ONE history entry: transform colors AND the palette itself (it lives in
    // renderSettings.palette), so a single undo fully reverts the apply —
    // previously the palette identity sat in signals and undo half-reverted
    // (colors back, palette grading still on).
    setFlameDescriptor((draft) => {
      applyColorMapToFlame(draft, colorMap)
      draft.renderSettings.palette = {
        id: palette.id,
        name: palette.name,
        entries: palette.entries.map(({ id, position, a, b }) => ({
          id,
          position,
          a,
          b,
        })),
      }
    }, 'Apply Palette')
  }

  const handlePaletteUnselect = () => {
    // One undoable entry: restore pre-palette colors + drop the palette.
    setFlameDescriptor((draft) => {
      const saved = prePaletteColors()
      for (const [tid, t] of recordEntries(draft.transforms)) {
        if (saved[tid]) {
          t.color = { x: saved[tid].x, y: saved[tid].y }
        }
      }
      delete draft.renderSettings.palette
    }, 'Remove Palette')
    setPrePaletteColors({})
  }

  // Shared by the toolbar Benchmark button and the `?benchmark` auto-open.
  const showBenchmark = createShowBenchmark()

  const showDocumentation = createShowDocumentation({
    hardwareTier: () => props.hardwareTier ?? null,
  })

  onMount(() => {
    trackAppInit(Boolean(window.navigator?.gpu))
    loadCustomVariations()
    setCustomVarsVersion((v) => v + 1)
    void loadRandomizerHistoryEntries(MAX_RANDOMIZER_HISTORY_LIMIT).then(
      setRandomizerHistory,
    )
    if (props.autoOpenBenchmark) {
      void showBenchmark({ autoStart: props.autoStartBenchmark })
    }
    if (IS_DEV) {
      console.info('[share:app] onMount', {
        hasQueryFlame: !!props.flameFromQuery?.flame,
        hasWelcomeFlame: !!props.flameFromWelcome?.(),
        selectedPaletteId: selectedPaletteId(),
      })
    }

    // Esc clears the transform selection (deselect-all → nothing dimmed).
    const handleSelectionKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedTransformId() !== null) {
        setSelectedTransformId(null)
      }
    }
    window.addEventListener('keydown', handleSelectionKeyDown)
    onCleanup(() => {
      window.removeEventListener('keydown', handleSelectionKeyDown)
    })
  })

  const setFlameZoom: Setter<number> = (value) => {
    // Editing the base camera detaches the held-frame preview (Blender-like).
    timeline.setPreviewHeld(false)
    if (typeof value === 'function') {
      setFlameDescriptor((draft) => {
        draft.renderSettings.camera.zoom = clamp(
          value(draft.renderSettings.camera.zoom),
          MIN_CAMERA_ZOOM_VALUE,
          MAX_CAMERA_ZOOM_VALUE,
        )
      })
    } else {
      setFlameDescriptor((draft) => {
        draft.renderSettings.camera.zoom = clamp(
          value,
          MIN_CAMERA_ZOOM_VALUE,
          MAX_CAMERA_ZOOM_VALUE,
        )
      })
    }
    return flameDescriptor.renderSettings.camera.zoom
  }
  const setFlamePosition: Setter<v2f> = (value) => {
    // Editing the base camera detaches the held-frame preview (Blender-like).
    timeline.setPreviewHeld(false)
    if (typeof value === 'function') {
      setFlameDescriptor((draft) => {
        const newPos = value(vec2f(...draft.renderSettings.camera.position))
        draft.renderSettings.camera.position = [newPos.x, newPos.y]
      })
    } else {
      setFlameDescriptor((draft) => {
        draft.renderSettings.camera.position = [value.x, value.y]
      })
    }
    return flameDescriptor.renderSettings.camera.position
  }

  // Build a Setter<number> for a uniform camera3D scalar: detach the held-frame
  // preview (Blender-like), apply the value/updater into the store, return the
  // result. theta/phi/radius/fov/roll were byte-for-byte identical modulo the
  // field; zoom (clamped), position (vec2) and target3D (vec3) stay bespoke.
  function makeCamera3DSetter(
    read: (c: FlameDescriptor['renderSettings']['camera3D']) => number,
    write: (draft: FlameDescriptor, next: number) => void,
  ): Setter<number> {
    return (value) => {
      timeline.setPreviewHeld(false)
      setFlameDescriptor((draft) => {
        const prev = read(draft.renderSettings.camera3D)
        write(
          draft,
          typeof value === 'function'
            ? (value as (p: number) => number)(prev)
            : value,
        )
      })
      return read(flameDescriptor.renderSettings.camera3D)
    }
  }
  const setFlameTheta = makeCamera3DSetter(
    (c) => c.theta,
    (d, v) => {
      d.renderSettings.camera3D.theta = v
    },
  )
  const setFlamePhi = makeCamera3DSetter(
    (c) => c.phi,
    (d, v) => {
      d.renderSettings.camera3D.phi = v
    },
  )
  const setFlameRadius = makeCamera3DSetter(
    (c) => c.radius,
    (d, v) => {
      d.renderSettings.camera3D.radius = v
    },
  )
  // 3D auto-exposure: drive the real Exposure value from the camera zoom so the
  // slider visibly tracks it. exposure = base + strength*log(radius/refRadius),
  // neutral at the radius where the toggle was enabled. The exposure read is
  // untracked so manual edits between zooms aren't immediately reverted.
  // The target is a pure derivation of the camera/auto-exposure settings, so it
  // lives in a memo; the effect's only job is to write it back (reading the
  // current exposure untracked so it never re-subscribes to its own output).
  const autoExposureTarget = createMemo<number | null>(() => {
    const rs = flameDescriptor.renderSettings
    if (!rs.autoExposure3D || (rs.dimensions ?? 2) !== 3) return null
    const radius = rs.camera3D?.radius ?? 0
    const ref = rs.autoExposure3DRefRadius
    if (radius <= 0 || ref <= 0) return null
    return (
      rs.autoExposure3DBase + rs.autoExposure3DStrength * Math.log(radius / ref)
    )
  })
  createEffect(() => {
    const target = autoExposureTarget()
    if (target === null) return
    const current = untrack(() => flameDescriptor.renderSettings.exposure)
    if (Math.abs(target - current) > 1e-4) {
      // Silent: this is a derived follower of the camera radius. Recording it
      // injected a fresh history entry whenever an undo reverted the radius
      // (effects run after the undo completes) — destroying redo and making
      // undo fight the user.
      history.setSilently((draft) => {
        draft.renderSettings.exposure = target
      })
    }
  })
  const setFlameTarget3D = (value: Vec3 | ((prev: Vec3) => Vec3)) => {
    setFlameDescriptor((draft) => {
      const newTarget =
        typeof value === 'function'
          ? value(new Float32Array(draft.renderSettings.camera3D.target))
          : value
      draft.renderSettings.camera3D.target = [
        newTarget[0] ?? 0,
        newTarget[1] ?? 0,
        newTarget[2] ?? 0,
      ]
    })
    return new Float32Array(flameDescriptor.renderSettings.camera3D.target)
  }
  const setFlameFov = makeCamera3DSetter(
    (c) => c.fov,
    (d, v) => {
      d.renderSettings.camera3D.fov = v
    },
  )
  const setFlameRoll = makeCamera3DSetter(
    (c) => c.roll,
    (d, v) => {
      d.renderSettings.camera3D.roll = v
    },
  )

  // First-person "fly" navigation for 3D flames. Session-only (you don't want
  // to reopen the app mid-flight); the movement speed is remembered.
  const [flyMode, setFlyMode] = createSignal(false)
  const flySpeed = persistentSignal('camera3D/fly-speed', 1)

  const effectiveTheta = () => {
    if (timeline.isDrivingView()) {
      const val = timeline.resolveValueAtPath(
        'camera3D.theta',
        timeline.currentFrame(),
      )
      if (val !== null && typeof val === 'number') return val
    }
    return flameDescriptor.renderSettings.camera3D.theta
  }
  const effectivePhi = () => {
    if (timeline.isDrivingView()) {
      const val = timeline.resolveValueAtPath(
        'camera3D.phi',
        timeline.currentFrame(),
      )
      if (val !== null && typeof val === 'number') return val
    }
    return flameDescriptor.renderSettings.camera3D.phi
  }
  const effectiveRadius = () => {
    if (timeline.isDrivingView()) {
      const val = timeline.resolveValueAtPath(
        'camera3D.radius',
        timeline.currentFrame(),
      )
      if (val !== null && typeof val === 'number') return val
    }
    return flameDescriptor.renderSettings.camera3D.radius
  }
  const effectiveTarget3D = () => {
    // Array properties are not easily animatable yet, so just use descriptor
    return new Float32Array(flameDescriptor.renderSettings.camera3D.target)
  }
  const effectiveRoll = () => {
    if (timeline.isDrivingView()) {
      const val = timeline.resolveValueAtPath(
        'camera3D.roll',
        timeline.currentFrame(),
      )
      if (val !== null && typeof val === 'number') return val
    }
    return flameDescriptor.renderSettings.camera3D.roll
  }
  const effectiveFov = () => {
    if (timeline.isDrivingView()) {
      const val = timeline.resolveValueAtPath(
        'camera3D.fov',
        timeline.currentFrame(),
      )
      if (val !== null && typeof val === 'number') return val
    }
    return flameDescriptor.renderSettings.camera3D.fov
  }

  // Per-mode flame memory: the dimension toggle stashes the active flame and
  // restores the one last used in the target mode, so 2D work is never lost
  // by exploring 3D (and vice versa). First entry into 3D loads a starter.
  // The animation tracks are stashed alongside, because keyframe paths are
  // dimension-specific (transform ids, camera vs camera3D, affine a–f vs a–l)
  // and carrying them across a 2D↔3D switch would orphan them.
  let stashedFlame2D: FlameDescriptor | undefined
  let stashedFlame3D: FlameDescriptor | undefined
  let stashedTracks2D: TimelineTrack[] | undefined
  let stashedTracks3D: TimelineTrack[] | undefined

  createEffect(() => {
    const progress = animationExportProgress()
    if (
      animationExportRunning() &&
      progress &&
      animationExportBackend() === 'local'
    ) {
      if (!timeline.isPlaying()) {
        timeline.setCurrentFrame(progress.currentTimelineFrame)
      }
    }
  })

  const onDrop = useAppDragAndDrop(history, setLoadedAnimation)

  const timeline = createTimelineState()
  // One chronological undo across flame history + timeline snapshots —
  // Ctrl+Z/Ctrl+Y and the toolbar buttons all route through this.
  const undoRouter = createUndoRouter(history, timeline)

  // Audio-reactive loop: plays audio through AudioContext, drives
  // renderSettings at 30fps synced to playback time.
  useAudioReactive(
    audioEnabled,
    audioBuffer,
    audioMapping,
    setFlameDescriptor,
    liveAnalyzer,
    audioSource,
    playbackPaused,
    seekTarget,
    setPlaybackTime,
    fileAnalyzer,
  )

  // Sonification loop: synthesizes audio in real-time from flame structure.
  useSonification(sonificationEnabled, sonificationConfig, flameDescriptor)

  /**
   * Capture the current flame as a downscaled PNG for OG link previews.
   *
   * Drives the same async export loop as the PNG export — via `setExportQuality`
   * at the *current* quality, so the on-screen canvas is unchanged. Unlike the
   * rAF loop, that loop renders even a fully settled flame and keeps running in
   * background tabs, so the capture reliably produces a frame (the earlier
   * hook-only path could time out and silently drop the preview). Captures the
   * clean, quality-graded image, then scales it down (aspect preserved).
   */
  async function captureOgImageBlob(maxDim = 1000): Promise<Blob | null> {
    const rawBlob = await new Promise<Blob | null>((resolve) => {
      let settled = false
      const finish = (b: Blob | null) => {
        if (settled) return
        settled = true
        setOnExportImage(undefined)
        setExportQuality(undefined)
        resolve(b)
      }
      // Best-effort with a generous safety net — never hang the share flow.
      const timer = setTimeout(() => {
        finish(null)
      }, 20000)
      setOnExportImage(
        () =>
          (canvas: HTMLCanvasElement, info?: { finalImageReady: boolean }) => {
            // Wait for the export driver's clean, quality-graded frame.
            if (info?.finalImageReady !== true) return
            clearTimeout(timer)
            canvas.toBlob(
              (b) => {
                finish(b)
              },
              'image/png',
              1,
            )
          },
      )
      // Same render path as PNG export; current quality keeps the canvas as-is.
      setExportQuality(qualityPresets[qualityPreset()])
    })
    if (!rawBlob) return null

    const url = URL.createObjectURL(rawBlob)
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const im = new Image()
        im.onload = () => {
          resolve(im)
        }
        im.onerror = reject
        im.src = url
      })
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height))
      const w = Math.max(1, Math.round(img.width * scale))
      const h = Math.max(1, Math.round(img.height * scale))
      const offscreen = document.createElement('canvas')
      offscreen.width = w
      offscreen.height = h
      const ctx = offscreen.getContext('2d')!
      ctx.drawImage(img, 0, 0, w, h)
      const downscaled = await new Promise<Blob | null>((resolve) => {
        offscreen.toBlob((b) => {
          resolve(b)
        }, 'image/png')
      })
      if (!downscaled) return null

      // Embed the flame descriptor into the PNG (deflate-compressed zTXt chunk,
      // a few KB) so anyone who opens the shared link or downloads the preview
      // image can load it straight back into the app — same as Discord sharing.
      const tracks = timeline.tracks()
      const config = timeline.config()
      const hasAnimation = tracks.some((track) => track.keyframes.length > 0)
      const payload = hasAnimation
        ? { flame: flameDescriptor, animation: { tracks, config } }
        : flameDescriptor
      const encoded = await compressJsonQueryParam(payload)
      const pngBytes = new Uint8Array(await downscaled.arrayBuffer())
      return addFlameDataToPng(encoded, pngBytes)
    } catch {
      return null
    } finally {
      URL.revokeObjectURL(url)
    }
  }

  const { showShareLinkModal } = createShareLinkModal(
    flameDescriptor,
    () => timeline.tracks(),
    () => timeline.config(),
    captureOgImageBlob,
  )

  const { showDiscordShareModal } = createDiscordShareModal()

  const { showImportVariationsModal } = createImportVariationsModal()

  const { showShareVariationLinkModal } = createShareVariationLinkModal()

  const { showShareVariationLoadModal } = createShareVariationLoadModal()

  const { showMigrationModal } = createMigrationModal(history)

  /** Waits until the canvas backing-store size stops changing (the resize is
   *  reactive and may be debounced) so export dimensions read a settled size. */
  async function waitForStableCanvasSize(
    canvas: HTMLCanvasElement,
    timeoutMs = 2000,
  ) {
    const startMs = Date.now()
    let lastWidth = -1
    let lastHeight = -1
    while (Date.now() - startMs < timeoutMs) {
      await new Promise<void>((resolve) =>
        setTimeout(() => {
          resolve()
        }, 60),
      )
      if (canvas.width === lastWidth && canvas.height === lastHeight) return
      lastWidth = canvas.width
      lastHeight = canvas.height
    }
  }

  async function startAnimationExport(
    config: AnimationExportConfig,
    _placeholderCanvas: HTMLCanvasElement,
  ) {
    const canvas = document.querySelector<HTMLCanvasElement>(`.${ui.canvas}`)
    if (!canvas) {
      showToast('Canvas not found')
      return
    }

    // Assert the export signals BEFORE the canvas-resize await below so the
    // server progress modal (opened right after this call returns) sees the
    // export as running instead of auto-closing; createAnimationExport
    // re-asserts and later clears them.
    let startupCancelled = false
    setAnimationExportRunning(true)
    setAnimationExportBackend(config.backend ?? 'local')
    setAnimationExportCancel(() => () => {
      startupCancelled = true
      setAnimationExportRunning(false)
      setAnimationExportBackend('local')
      setAnimationExportCancel(undefined)
    })

    // True high-resolution export: render the canvas backing store at the exact
    // export dimensions (resolution + aspect) for the duration of the export,
    // instead of bitmap-upscaling the viewport canvas (which only interpolated
    // pixels and produced soft output).
    setExportDimensions({ width: config.width, height: config.height })
    await waitForStableCanvasSize(canvas)

    if (startupCancelled) {
      setExportDimensions(undefined)
      return
    }

    try {
      // The canvas already renders at the export dimensions.
      const { promise } = createAnimationExport(
        config,
        canvas,
        timeline,
        flameDescriptor,
        // Silent writer: the export applies animated state once PER FRAME —
        // recording it buried the user's real edits under hundreds of
        // per-frame history entries (uncapped stack).
        history.setSilently,
        setOnExportImage,
      )

      promise
        .then((blob) => {
          if (blob.size === 0) return // cancelled
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = 'animation.mp4'
          a.click()
          URL.revokeObjectURL(url)
          showToast('Animation exported')
        })
        .catch((err: unknown) => {
          console.error('Animation export failed:', err)
          showToast('Animation export failed')
        })
        .finally(() => {
          setExportDimensions(undefined)
        })
    } catch (err: unknown) {
      console.error('Animation export startup failed:', err)
      showToast('Animation export failed')
      setAnimationExportRunning(false)
      setAnimationExportBackend('local')
      setAnimationExportCancel(undefined)
      setExportDimensions(undefined)
    }
  }

  const { show: showServerRenderDialog } = createServerRenderDialog(
    () => flameDescriptor,
  )

  const { showExportPngDialog, quickExport, exportModalIsOpen } =
    createExportPngDialog(
      flameDescriptor,
      () => timeline,
      pixelRatio,
      setPixelRatio,
      setOnExportImage,
      setFlameDescriptor,
      () => selectedPalette(),
      () => {
        const canvas = document.querySelector<HTMLCanvasElement>(
          `.${ui.canvas}`,
        )
        if (canvas && canvas.clientWidth > 0 && canvas.clientHeight > 0) {
          return canvas.clientWidth / canvas.clientHeight
        }
        return window.innerWidth / window.innerHeight
      },
      enqueueImageJob,
      enqueueAnimationJob,
      startAnimationExport,
      () => blendFlame(),
      () => resolvedBlendWeight(),
      () => audioBuffer(),
      () => audioMapping().mappings,
      () =>
        auth.featureFlags().serverRendering
          ? () => {
              if (timeline.isPlaying()) timeline.pause()
              void showServerRenderDialog()
            }
          : undefined,
    )

  async function shareToDiscord() {
    // Step 1: Capture the current flame at its current resolution to prevent flickering/resizing
    const rawBlob = await new Promise<Blob | null>((resolve) => {
      setOnExportImage(() => (canvas: HTMLCanvasElement) => {
        setOnExportImage(undefined)
        canvas.toBlob(
          (b) => {
            resolve(b)
          },
          'image/png',
          1,
        )
      })
    })

    if (!rawBlob) {
      showToast('Failed to capture flame image')
      return
    }

    // Step 2: Embed flame data into the PNG so it can be loaded back
    const tracks = timeline.tracks()
    const config = timeline.config()
    const hasAnimation = tracks.some((track) => track.keyframes.length > 0)
    const payload = hasAnimation
      ? { flame: flameDescriptor, animation: { tracks, config } }
      : flameDescriptor
    const encoded = await compressJsonQueryParam(payload)
    let pngBytes = new Uint8Array(await rawBlob.arrayBuffer())
    pngBytes = new Uint8Array(
      await addFlameDataToPng(encoded, pngBytes).arrayBuffer(),
    )
    const blob = new Blob([pngBytes], { type: 'image/png' })

    // Step 3: Show the modal — it drives the send (with Turnstile) and offers a
    // manual download / copy-link fallback if the direct post fails.
    const previewUrl = URL.createObjectURL(blob)

    // Build a proper share link via the same path as the Share Link modal
    // (short `?s=` link when available, inline `?flame=` link otherwise) so the
    // fallback "Copy share link" is instant and correct. Runs in parallel; the
    // OG preview upload is best-effort so the copied link shows a rich card.
    const sharePromise = createShareLink({
      flame: flameDescriptor,
      animation: hasAnimation ? { tracks, config } : undefined,
    })
    void sharePromise.then(async ({ encoded: shareEncoded }) => {
      const ogBlob = await captureOgImageBlob()
      if (!ogBlob) return
      const { title, description } = deriveOgMeta(flameDescriptor)
      void uploadOgPreview({
        encoded: shareEncoded,
        blob: ogBlob,
        title,
        description,
      })
    })

    const shared = await showDiscordShareModal({
      previewUrl,
      initialMetadata: flameDescriptor.metadata,
      onShare: (meta, token) => sendFlameToDiscord(blob, meta, token),
      onDownload: () => {
        downloadBlob(blob, 'flame.png')
      },
      onCopyLink: async () => {
        try {
          const { primaryUrl } = await sharePromise
          await globalThis.navigator.clipboard.writeText(primaryUrl)
          return true
        } catch {
          return false
        }
      },
      discordUrl: '/discord',
    })
    URL.revokeObjectURL(previewUrl)
    if (shared) showToast('Shared to Discord')
  }

  const { showLogoFaviconGenerator } = createLogoFaviconGenerator(
    flameDescriptor,
    () => selectedPalette(),
    history,
  )

  const [randomizerHistory, setRandomizerHistory] = createSignal<
    RandomizerHistoryEntry[]
  >([])

  const [selectedHistoryTimestamp, setSelectedHistoryTimestamp] =
    createSignal<number>(0)

  const handleClearHistory = async () => {
    await clearRandomizerHistory()
    setRandomizerHistory([])
  }

  function captureMainThumbnail(size: number): Promise<string | null> {
    const canvas = document.querySelector<HTMLCanvasElement>(`.${ui.canvas}`)
    if (canvas === null) return Promise.resolve(null)
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob === null) {
          resolve(null)
          return
        }
        const url = URL.createObjectURL(blob)
        const img = new Image()
        img.onload = () => {
          const offscreen = document.createElement('canvas')
          offscreen.width = size
          offscreen.height = size
          const ctx = offscreen.getContext('2d')!
          ctx.drawImage(img, 0, 0, size, size)
          URL.revokeObjectURL(url)
          resolve(offscreen.toDataURL('image/png'))
        }
        img.onerror = () => {
          URL.revokeObjectURL(url)
          resolve(null)
        }
        img.src = url
      }, 'image/png')
    })
  }

  type RandomizeSettings = {
    skipIters: boolean
    skipItersRange?: [number, number]
    exposure: boolean
    exposureRange?: [number, number]
    contrast: boolean
    contrastRange?: [number, number]
    gamma: boolean
    gammaRange?: [number, number]
    highlightPower: boolean
    highlightPowerRange?: [number, number]
    vibrancy: boolean
    vibrancyRange?: [number, number]
  }
  // Apply the randomizer's per-field "randomize this setting" toggles onto a
  // render-settings object. Extracted so Generate and Mutate share one source
  // of truth — the two copies were byte-identical and would silently drift.
  const applyRandomizeSettings = (
    rs: FlameDescriptor['renderSettings'],
    s: RandomizeSettings,
  ): void => {
    if (s.skipIters) {
      const r = s.skipItersRange ?? [5, 30]
      rs.skipIters = Math.floor(randomRange(r[0], r[1] + 1))
    }
    if (s.exposure) {
      const r = s.exposureRange ?? [-2, 2]
      rs.exposure = randomRange(r[0], r[1])
    }
    if (s.contrast) {
      const r = s.contrastRange ?? [0.5, 4.0]
      rs.contrast = randomRange(r[0], r[1])
    }
    if (s.gamma) {
      const r = s.gammaRange ?? [1.0, 3.5]
      rs.gamma = randomRange(r[0], r[1])
    }
    if (s.highlightPower) {
      const r = s.highlightPowerRange ?? [0.1, 0.9]
      rs.highlightPower = randomRange(r[0], r[1])
    }
    if (s.vibrancy) {
      const r = s.vibrancyRange ?? [0.2, 0.8]
      rs.vibrancy = randomRange(r[0], r[1])
    }
  }
  const runGenerateFlame = async (
    config: GenerateRandomFlameConfig,
    randomizeSettings: RandomizeSettings,
    recordHistory: boolean,
  ) => {
    if (recordHistory) {
      const thumb = await captureMainThumbnail(128)
      if (thumb) {
        const entry: RandomizerHistoryEntry = {
          flame: deepClone(flameDescriptor),
          thumbnail: thumb,
          timestamp: Date.now(),
        }
        const updated = await addRandomizerHistoryEntry(
          entry,
          MAX_RANDOMIZER_HISTORY_LIMIT,
        )
        setRandomizerHistory(updated)
      }
    }

    setSelectedHistoryTimestamp(0)

    const newFlame = generateRandomFlame(config)
    const prevRs = flameDescriptor.renderSettings
    const rs = deepClone(prevRs)

    applyRandomizeSettings(rs, randomizeSettings)

    newFlame.renderSettings = rs
    history.replace(newFlame, 'Randomize Flame')
  }

  const runMutateFlame = async (
    config: GenerateRandomFlameConfig,
    randomizeSettings: RandomizeSettings,
    mutationSettings: MutateFlameOptions,
    recordHistory: boolean,
  ) => {
    if (recordHistory) {
      const thumb = await captureMainThumbnail(128)
      if (thumb) {
        const entry: RandomizerHistoryEntry = {
          flame: deepClone(flameDescriptor),
          thumbnail: thumb,
          timestamp: Date.now(),
        }
        const updated = await addRandomizerHistoryEntry(
          entry,
          MAX_RANDOMIZER_HISTORY_LIMIT,
        )
        setRandomizerHistory(updated)
      }
    }

    setSelectedHistoryTimestamp(0)

    const mutatedFlame = mutateFlame(flameDescriptor, config, mutationSettings)
    const prevRs = flameDescriptor.renderSettings
    const rs = deepClone(prevRs)

    applyRandomizeSettings(rs, randomizeSettings)

    mutatedFlame.renderSettings = rs
    history.replace(mutatedFlame, 'Mutate Flame')
  }

  // Keep the randomizer card visually fixed across a flame swap. Changing the
  // transform count reflows the sidebar (affine/colour list rows + transform
  // cards), which would otherwise shove the Generate button up/down under the
  // cursor. Measure the card before, correct scrollTop once the DOM has settled.
  const anchorSidebarToRandomizer = (): (() => void) => {
    if (!sidebarScrollRef || !randomizerCardRef) return () => {}
    const before = randomizerCardRef.getBoundingClientRect().top
    return () => {
      requestAnimationFrame(() => {
        if (!sidebarScrollRef || !randomizerCardRef) return
        sidebarScrollRef.scrollTop +=
          randomizerCardRef.getBoundingClientRect().top - before
      })
    }
  }

  // Timeline "Animate" button: reveal the sidebar (may be closed, auto-hidden
  // on mobile, or covered by the blend gallery / quick variation picker), open
  // the Flame Randomizer card with its Animation Settings section expanded,
  // and scroll the card into view. Overlay dismissal must happen BEFORE the
  // epoch bump: mounting the card swallows the current epoch as its initial
  // value, so a bump-then-mount order would lose the expansion.
  const openAnimationGenerator = () => {
    setShowSidebar(true)
    setSidebarHidden(false)
    setShowBlendGallery(false)
    setQuickPickState(null)
    setRandomizerOpen(true)
    setRandomizerAnimEpoch((e) => e + 1)
    setTimeout(() => {
      randomizerCardRef?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 0)
  }

  // Guard randomize/mutate so a slow run (history thumbnail capture + render)
  // can't be re-triggered until it finishes — rapid clicks would otherwise pile
  // up concurrent captures and lag the UI. Mirrors the logo/favicon generator.
  const handleGenerateFlame = async (
    ...args: Parameters<typeof runGenerateFlame>
  ) => {
    if (isRandomizing()) return
    setIsRandomizing(true)
    const releaseAnchor = anchorSidebarToRandomizer()
    try {
      await runGenerateFlame(...args)
    } finally {
      setIsRandomizing(false)
      releaseAnchor()
    }
  }

  const handleMutateFlame = async (
    ...args: Parameters<typeof runMutateFlame>
  ) => {
    if (isRandomizing()) return
    setIsRandomizing(true)
    const releaseAnchor = anchorSidebarToRandomizer()
    try {
      await runMutateFlame(...args)
    } finally {
      setIsRandomizing(false)
      releaseAnchor()
    }
  }

  const handleUpdateRenderSettings = (
    settings: Partial<FlameDescriptor['renderSettings']>,
  ) => {
    setFlameDescriptor((draft) => {
      draft.renderSettings = {
        ...draft.renderSettings,
        ...settings,
      }
    })
  }

  // Deleting a custom variation the CURRENT flame uses breaks its rendering,
  // and the library lives outside the flame's undo history — so confirm when
  // referenced, and always offer recovery through the toast's Undo action.
  const handleDeleteCustomVariation = async (def: CustomVariationDef) => {
    const usedByFlame = Object.values(flameDescriptor.transforms).some(
      (transform) =>
        Object.values(transform.variations).some(
          (variation) => variation.type === def.id,
        ),
    )
    if (usedByFlame) {
      const confirmed = await _requestModal<boolean>({
        content: ({ respond }) => (
          <ConfirmDeleteVariationModal name={def.name} respond={respond} />
        ),
      })
      if (!confirmed) return
    }
    deleteCustomVariation(def.id)
    setCustomVarsVersion((v) => v + 1)
    showToast(`Deleted custom variation "${def.name}"`, 10000, [
      {
        label: 'Undo',
        onClick: () => {
          if (restoreCustomVariation(def)) {
            setCustomVarsVersion((v) => v + 1)
            showToast(`Restored "${def.name}"`)
          } else {
            showToast(`Could not restore "${def.name}"`)
          }
        },
      },
    ])
  }

  const handleLoadHistory = (entry: RandomizerHistoryEntry) => {
    setSelectedHistoryTimestamp(entry.timestamp)
    // Loading a history entry is a fresh starting point: keep unsaved work
    // recoverable and don't autosave the untouched loaded flame.
    flushDirtyToRecents()
    history.replace(deepClone(entry.flame), 'Load History Flame')
    markLoadedBaseline()
  }

  const handleRandomizeAnimation = (
    presetIds: string[],
    clearFirst: boolean,
  ) => {
    if (presetIds.length === 0) return

    isRandomizingAnimation = true
    try {
      // One click = one undo step, regardless of how many keyframes the
      // selected presets write (previously each addKeyframe pushed its own
      // snapshot — dozens of Ctrl+Z to revert, and enough to overflow the
      // undo cap and lose the pre-click animation entirely).
      timeline.runWithSingleUndo(() => {
        randomizeAnimationTracks(presetIds, clearFirst)
      })
      setShowTimeline(true)
    } finally {
      setTimeout(() => {
        isRandomizingAnimation = false
      }, 200)
    }
  }

  const randomizeAnimationTracks = (
    presetIds: string[],
    clearFirst: boolean,
  ) => {
    {
      if (clearFirst) timeline.clearAllTracks()

      const start = timeline.config().startFrame
      const end = timeline.config().endFrame
      const mid = Math.floor((start + end) / 2)

      const addLoopingTrack = (
        paramPath: string,
        startVal: number,
        minPerturb: number,
        maxPerturb: number,
        easing: EasingCurve = 'easeInOut',
      ) => {
        const perturb =
          randomRange(minPerturb, maxPerturb) * (Math.random() > 0.5 ? 1 : -1)
        const midVal = startVal + perturb
        timeline.addKeyframe(paramPath, start, startVal, easing)
        timeline.addKeyframe(paramPath, mid, midVal, easing)
        timeline.addKeyframe(paramPath, end, startVal, easing)
      }

      const addContinuousTrack = (
        paramPath: string,
        startVal: number,
        delta: number,
      ) => {
        timeline.addKeyframe(paramPath, start, startVal, 'linear')
        timeline.addKeyframe(paramPath, end, startVal + delta, 'linear')
      }

      for (const preset of presetIds) {
        if (preset === 'pan') {
          const camX = flameDescriptor.renderSettings.camera?.position?.[0] ?? 0
          const camY = flameDescriptor.renderSettings.camera?.position?.[1] ?? 0
          addLoopingTrack('camera.x', camX, 0.1, 0.4)
          addLoopingTrack('camera.y', camY, 0.1, 0.4)
        } else if (preset === 'zoom') {
          const zoom = flameDescriptor.renderSettings.camera?.zoom ?? 1
          addLoopingTrack('camera.zoom', zoom, zoom * 0.15, zoom * 0.4)
        } else if (preset === 'rot') {
          const rot = flameDescriptor.renderSettings.camera?.rotation ?? 0
          const dir = Math.random() > 0.5 ? 1 : -1
          addContinuousTrack('camera.rotation', rot, dir * 2 * Math.PI)
        } else if (preset === 'color') {
          const phase = flameDescriptor.renderSettings.palettePhase ?? 0
          const dir = Math.random() > 0.5 ? 1 : -1
          addContinuousTrack('palettePhase', phase, dir * randomRange(1, 3))
        } else if (preset === 'transformColor') {
          // Drift each transform's OkLab (a, b) color coordinate in a loop —
          // animates the per-transform colors (the color scrub inputs), distinct
          // from palette cycling above.
          for (const [tid, t] of Object.entries(flameDescriptor.transforms)) {
            addLoopingTrack(`transform.${tid}.color.x`, t.color.x, 0.1, 0.3)
            addLoopingTrack(`transform.${tid}.color.y`, t.color.y, 0.1, 0.3)
          }
        } else if (preset === 'vibrancy') {
          const vib = flameDescriptor.renderSettings.vibrancy ?? 0.5
          const minPert = vib > 0.5 ? -0.3 : 0.1
          const maxPert = vib > 0.5 ? -0.1 : 0.3
          addLoopingTrack('vibrancy', vib, minPert, maxPert)
        } else if (preset === 'orbit') {
          const theta = flameDescriptor.renderSettings.camera3D?.theta ?? 0
          const phi =
            flameDescriptor.renderSettings.camera3D?.phi ?? Math.PI / 2
          const radius = flameDescriptor.renderSettings.camera3D?.radius ?? 5

          addContinuousTrack('camera3D.theta', theta, 2 * Math.PI)
          addLoopingTrack('camera3D.phi', phi, 0.1, 0.3)
          addLoopingTrack(
            'camera3D.radius',
            radius,
            radius * 0.1,
            radius * 0.25,
          )
        } else if (preset === 'finalTransform') {
          const q1 = Math.floor(start + (end - start) * 0.25)
          const q3 = Math.floor(start + (end - start) * 0.75)
          const dir = Math.random() > 0.5 ? 1 : -1

          // Rotate the final transform a full turn. The affine matrix is
          // [[a, b], [d, e]] (c, f are translation), so rotation by θ is
          // a = e = cos θ, b = -sin θ, d = sin θ (`dir` flips the spin).
          // θ steps 0,90,180,270,360 → cos: 1,0,-1,0,1  sin: 0,1,0,-1,0
          timeline.addKeyframe('finalTransform.a', start, 1, 'linear')
          timeline.addKeyframe('finalTransform.a', q1, 0, 'linear')
          timeline.addKeyframe('finalTransform.a', mid, -1, 'linear')
          timeline.addKeyframe('finalTransform.a', q3, 0, 'linear')
          timeline.addKeyframe('finalTransform.a', end, 1, 'linear')

          timeline.addKeyframe('finalTransform.e', start, 1, 'linear')
          timeline.addKeyframe('finalTransform.e', q1, 0, 'linear')
          timeline.addKeyframe('finalTransform.e', mid, -1, 'linear')
          timeline.addKeyframe('finalTransform.e', q3, 0, 'linear')
          timeline.addKeyframe('finalTransform.e', end, 1, 'linear')

          timeline.addKeyframe('finalTransform.b', start, 0, 'linear')
          timeline.addKeyframe('finalTransform.b', q1, -dir, 'linear')
          timeline.addKeyframe('finalTransform.b', mid, 0, 'linear')
          timeline.addKeyframe('finalTransform.b', q3, dir, 'linear')
          timeline.addKeyframe('finalTransform.b', end, 0, 'linear')

          timeline.addKeyframe('finalTransform.d', start, 0, 'linear')
          timeline.addKeyframe('finalTransform.d', q1, dir, 'linear')
          timeline.addKeyframe('finalTransform.d', mid, 0, 'linear')
          timeline.addKeyframe('finalTransform.d', q3, -dir, 'linear')
          timeline.addKeyframe('finalTransform.d', end, 0, 'linear')
        }
      }

      timeline.setAnimationEnabled(true)
    }
  }

  // Smart animation: apply a random curated preset from each category for a full
  // multi-aspect loop (vs the selected-items random tracks above). Honors the
  // same clear-first toggle.
  const handleSmartAnimation = (clearFirst: boolean) => {
    isRandomizingAnimation = true
    try {
      // One click = one undo step (see handleRandomizeAnimation).
      timeline.runWithSingleUndo(() => {
        if (clearFirst) timeline.clearAllTracks()
        smartRandomAnimation(flameDescriptor, timeline)
      })
      setShowTimeline(true)
    } finally {
      setTimeout(() => {
        isRandomizingAnimation = false
      }, 200)
    }
  }

  const runTourCommand: { fn?: (id: string, ...args: unknown[]) => void } = {}

  /** Active animateValue loops -- each entry snaps to its end value when called. */
  const activeAnimations = new Set<() => void>()

  const tourContext: TourContext = {
    setSidebarOpen: setShowSidebar,
    sidebarOpen: showSidebar,
    setTimelineOpen: setShowTimeline,
    timelineOpen: showTimeline,
    setAnimationEnabled,
    animationEnabled,
    openModal: (name) => {
      if (timeline.isPlaying()) timeline.pause()
      switch (name) {
        case 'loadFlame':
          void showLoadFlameModal()
          break
        case 'exportPng':
          void showExportPngDialog()
          break
        case 'shareLink':
          void showShareLinkModal()
          break
      }
    },
    closeCurrentModal: () => {},
    scrollToTarget: (selector) => {
      document
        .querySelector(selector)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
    executeCommand: (id, ...args) => {
      console.info(
        '[tourContext:executeCommand]',
        id,
        'args:',
        ...args,
        'fn:',
        !!runTourCommand.fn,
      )
      runTourCommand.fn?.(id, ...args)
    },
    animateValue: (start, end, durationMs, onUpdate) => {
      let cancelled = false
      const startTime = window.performance.now()

      function loop(currentTime: number) {
        if (cancelled) return
        const elapsed = currentTime - startTime
        if (elapsed >= durationMs) {
          onUpdate(end)
          activeAnimations.delete(finish)
          return
        }
        // Smooth ease-out cubic
        const t = Math.min(1, elapsed / durationMs)
        const eased = 1 - Math.pow(1 - t, 3)
        onUpdate(start + (end - start) * eased)
        requestAnimationFrame(loop)
      }

      function finish() {
        if (!cancelled) {
          cancelled = true
          onUpdate(end)
        }
        activeAnimations.delete(finish)
      }

      activeAnimations.add(finish)
      requestAnimationFrame(loop)
      return finish
    },
    finishAllAnimations: () => {
      // Snap every running animation to its end value
      for (const finish of activeAnimations) {
        finish()
      }
      activeAnimations.clear()
    },
    snapshotFlame: () => {
      return deepClone(flameDescriptor)
    },
    restoreFlame: (snapshot: unknown) => {
      // Use history.replace() which calls the raw setStore(reconcile(value))
      // directly. We cannot use setFlameDescriptor(reconcile(...)) because
      // setFlameDescriptor is a HistorySetter that wraps calls in
      // produceWithPatches (structurajs draft proxy), and reconcile expects
      // a SolidJS store proxy -- mixing the two causes "node.$ is not a
      // function".
      history.replace(snapshot as FlameDescriptor, 'tour:restore')
    },
  }

  const readableIds = createMemo(() =>
    buildReadableIds(flameDescriptor.transforms),
  )

  // Sync animation enabled state into timeline so diamonds can gate on it
  createEffect(() => {
    const enabled = animationEnabled()
    if (IS_DEV) {
      console.info(
        '[anim] sync effect: setting timeline.animationEnabled →',
        enabled,
      )
    }
    timeline.setAnimationEnabled(enabled)
  })

  // Apply animation tracks when loaded from the LoadFlame modal
  createEffect(() => {
    const anim = loadedAnimation()
    if (!anim) return
    if (anim.tracks.length === 0) {
      // Plain flame loaded — clear animation state
      if (IS_DEV) console.info('[anim] clearing tracks — plain flame loaded')
      timeline.loadTracks([])
      timeline.setIsPlaying(false)
      timeline.setAnimationEnabled(false)
      setAnimationEnabled(false)
      setShowTimeline(false)
    } else {
      if (IS_DEV) {
        console.info(
          '[anim] loading animation with',
          anim.tracks.length,
          'tracks:',
          anim.tracks.map((t) => t.parameterPath),
        )
      }
      timeline.loadTracks(anim.tracks)
      timeline.setAnimationEnabled(true)
      setAnimationEnabled(true)
      setShowTimeline(true)
      timeline.setConfig({ ...timeline.config(), loop: true })
      timeline.goToFrame(0)
      timeline.play()
      showToast(
        `Animation loaded: ${anim.tracks.length} track${anim.tracks.length !== 1 ? 's' : ''} — ${anim.tracks.length * 2} keyframes`,
        3500,
      )
    }
    // Clear the signal so re-selecting the same animation triggers again
    clearLoadedAnimation()
    // A load is a fresh starting point, not an edit — reset dirty tracking.
    markLoadedBaseline()
  })

  // ── Autosave & save-awareness ──────────────────────────────────────────
  // Baseline JSON of the last loaded/saved state; the flame is "dirty" when
  // the current state differs. Loads reset the baseline (and the editing
  // clock), so untouched examples are never autosaved; any edit diverges.
  // Every fresh starting point also rotates the autosave id, so a new
  // load/flame can never clobber the previous flame's autosave entry.
  const newAutosaveId = () =>
    `autosave-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  let autosaveSessionId = newAutosaveId()
  const autosaveSnapshot = () =>
    JSON.stringify({ flame: flameDescriptor, tracks: timeline.tracks() })
  let autosaveBaseline = autosaveSnapshot()
  let editingSince: number | null = null
  let lastAutosaveAt = 0
  let reminderShown = false
  let autosavePromptShown = false

  const isFlameDirty = () => autosaveSnapshot() !== autosaveBaseline
  const markSavedBaseline = () => {
    autosaveBaseline = autosaveSnapshot()
  }
  const markLoadedBaseline = () => {
    autosaveBaseline = autosaveSnapshot()
    editingSince = null
    autosaveSessionId = newAutosaveId()
  }

  const autosaveNow = () => {
    const saved = upsertRecentFlame(
      autosaveSessionId,
      flameDescriptor,
      undefined,
      timeline.tracks(),
    )
    // A failed write (quota, private mode) must not mark the flame clean —
    // the pagehide fallback would then skip it and the work would vanish.
    if (!saved) return
    lastAutosaveAt = Date.now()
    markSavedBaseline()
  }

  // Flush outgoing dirty work before the flame gets replaced (load, New
  // Flame, 2D/3D switch): the replace resets the baseline, after which the
  // pagehide safety net no longer sees the old state as dirty.
  const flushDirtyToRecents = () => {
    if (isFlameDirty()) autosaveNow()
  }

  // Reload/close/freeze with unsaved changes: persist silently — no prompt,
  // the work just shows up in Recent flames. Also saves on bfcache freezes
  // (`persisted`): frozen pages are routinely evicted without another
  // pagehide, and the upsert is idempotent when the page is restored.
  // Independent of the periodic-autosave setting.
  const saveOnPagehide = () => {
    flushDirtyToRecents()
  }
  window.addEventListener('pagehide', saveOnPagehide)
  onCleanup(() => {
    window.removeEventListener('pagehide', saveOnPagehide)
  })

  const AUTOSAVE_POLL_MS = 30_000
  const REMINDER_AFTER_MS = 5 * 60_000
  const autosavePoll = setInterval(() => {
    const dirty = isFlameDirty()
    if (dirty && editingSince === null) editingSince = Date.now()

    // First time an edit would be autosaved: ask once, remember the answer.
    if (dirty && autosaveRecents() === 'unset' && !autosavePromptShown) {
      autosavePromptShown = true
      showToast('Auto-save your flames to Recents while you edit?', 15000, [
        {
          label: 'Yes',
          onClick: () => {
            setAutosaveRecents('on')
            flushDirtyToRecents()
          },
        },
        { label: 'No', onClick: () => setAutosaveRecents('off') },
      ])
      return
    }

    if (dirty && autosaveRecents() === 'on') {
      const intervalMs = Math.max(1, autosaveIntervalMin()) * 60_000
      if (Date.now() - lastAutosaveAt >= intervalMs) autosaveNow()
    }

    // Gentle one-time pointer to saving/exporting after sustained editing.
    if (
      !reminderShown &&
      !saveReminderDismissed() &&
      editingSince !== null &&
      Date.now() - editingSince >= REMINDER_AFTER_MS
    ) {
      reminderShown = true
      showToast(
        'Enjoying this flame? Save it for later, export a PNG, or share a link from the actions bar.',
        12000,
        [
          {
            label: "Don't show again",
            onClick: () => setSaveReminderDismissed(true),
          },
        ],
      )
    }
  }, AUTOSAVE_POLL_MS)
  onCleanup(() => {
    clearInterval(autosavePoll)
  })

  // Apply flame and animation from shared URL (fires once when resource resolves)
  let queryApplied = false
  createEffect(() => {
    const data = props.flameFromQuery
    if (!data || queryApplied) return
    queryApplied = true

    if (IS_DEV) console.info('[share] applying flame from shared URL')
    history.replace(deepClone(data.flame))

    if (data.animation && data.animation.tracks.length > 0) {
      if (IS_DEV) {
        console.info(
          '[anim] loading shared animation:',
          data.animation.tracks.length,
          'tracks',
        )
      }
      timeline.loadTracks(data.animation.tracks)
      timeline.setAnimationEnabled(true)
      setAnimationEnabled(true)
      timeline.setConfig({
        ...timeline.config(),
        ...data.animation.config,
      })
      timeline.goToFrame(0)
      timeline.play()
    }
    // A shared link is a fresh starting point for dirty tracking.
    markLoadedBaseline()

    // Offer to save any custom variations the link brought in. They are already
    // registered (transiently) so the flame renders; this only asks which to
    // persist into the recipient's library. Variations whose code the user
    // already has are surfaced as "already in your library" (not re-saved).
    const imported = data.importedCustomVariations ?? []
    const alreadyOwned = data.alreadyOwnedCustomVariations ?? []
    if (imported.length > 0) {
      void (async () => {
        const selectedIds = await showImportVariationsModal(
          imported,
          alreadyOwned,
        )
        if (selectedIds && selectedIds.length > 0) {
          persistSharedVariations(selectedIds)
          setCustomVarsVersion((v) => v + 1)
          showToast(
            `Saved ${selectedIds.length} custom variation${selectedIds.length === 1 ? '' : 's'} to your library`,
          )
        }
      })()
    } else if (alreadyOwned.length > 0) {
      showToast(
        `${alreadyOwned.length} custom variation${alreadyOwned.length === 1 ? '' : 's'} from this flame ${alreadyOwned.length === 1 ? 'is' : 'are'} already in your library`,
      )
    }
  })

  // A single custom variation arrived via a `?cv=` link: preview it and offer to
  // save (fires once when the resource resolves).
  let sharedVariationApplied = false
  createEffect(() => {
    const sv = props.sharedVariationFromQuery
    if (!sv || sharedVariationApplied) return
    sharedVariationApplied = true
    void (async () => {
      const save = await showShareVariationLoadModal(sv.def, sv.alreadyOwned)
      if (save && !sv.alreadyOwned) {
        persistSharedVariations([sv.def.id])
        setCustomVarsVersion((v) => v + 1)
        showToast(`Saved "${sv.def.name}" to your library`)
      }
    })()
  })

  function getFlameValue(
    path: string,
  ):
    | number
    | string
    | [number, number, number]
    | [number, number, number, number]
    | null {
    const fd = flameDescriptor
    switch (path) {
      case 'exposure':
        return fd.renderSettings.exposure
      case 'skipIters':
        return fd.renderSettings.skipIters
      case 'plotsPerChain':
        return fd.renderSettings.plotsPerChain
      case 'vibrancy':
        return fd.renderSettings.vibrancy
      case 'contrast':
        return fd.renderSettings.contrast ?? 1
      case 'gamma':
        return fd.renderSettings.gamma ?? 2.2
      case 'highlightPower':
        return fd.renderSettings.highlightPower ?? 1
      case 'drawMode':
        return fd.renderSettings.drawMode
      case 'colorInitMode':
        return fd.renderSettings.colorInitMode
      case 'pointInitMode':
        return fd.renderSettings.pointInitMode
      case 'densityEstimationQuality':
        return fd.renderSettings.densityEstimationQuality ?? 0.8
      case 'estimatorCurve':
        return fd.renderSettings.estimatorCurve ?? 0.5
      case 'paletteMode':
        return fd.renderSettings.paletteMode ?? 0
      case 'palettePhase':
        return fd.renderSettings.palettePhase ?? 0
      case 'paletteSpeed':
        return fd.renderSettings.paletteSpeed ?? 1
      case 'backgroundColor':
        return fd.renderSettings.backgroundColor ?? [0, 0, 0]
      case 'edgeFadeColor':
        return fd.renderSettings.edgeFadeColor ?? [0, 0, 0, 0]
      case 'camera.x':
        if (timeline.isDrivingView()) {
          if (
            timeline.hasKeyframeAtFrame('camera.x', timeline.currentFrame())
          ) {
            const xTrack = timeline
              .tracks()
              .find((t) => t.parameterPath === 'camera.x')
            if (xTrack) {
              const val = resolveKeyframeValue(
                xTrack.keyframes,
                timeline.currentFrame(),
              )
              if (val !== null && typeof val === 'number') return val
            }
          }
        }
        return fd.renderSettings.camera?.position[0] ?? 0
      case 'camera.y':
        if (timeline.isDrivingView()) {
          if (
            timeline.hasKeyframeAtFrame('camera.y', timeline.currentFrame())
          ) {
            const yTrack = timeline
              .tracks()
              .find((t) => t.parameterPath === 'camera.y')
            if (yTrack) {
              const val = resolveKeyframeValue(
                yTrack.keyframes,
                timeline.currentFrame(),
              )
              if (val !== null && typeof val === 'number') return val
            }
          }
        }
        return fd.renderSettings.camera?.position[1] ?? 0
      case 'camera.zoom':
        if (timeline.isDrivingView()) {
          if (
            timeline.hasKeyframeAtFrame('camera.zoom', timeline.currentFrame())
          ) {
            const zoomTrack = timeline
              .tracks()
              .find((t) => t.parameterPath === 'camera.zoom')
            if (zoomTrack) {
              const val = resolveKeyframeValue(
                zoomTrack.keyframes,
                timeline.currentFrame(),
              )
              if (val !== null && typeof val === 'number') return val
            }
          }
        }
        return fd.renderSettings.camera?.zoom ?? 1
      case 'camera.rotation':
        return (
          ((fd.renderSettings.camera as Record<string, unknown> | undefined)
            ?.rotation as number | undefined) ?? 0
        )
      case 'camera3D.theta':
        if (timeline.isDrivingView()) {
          if (
            timeline.hasKeyframeAtFrame(
              'camera3D.theta',
              timeline.currentFrame(),
            )
          ) {
            const track = timeline
              .tracks()
              .find((t) => t.parameterPath === 'camera3D.theta')
            if (track) {
              const val = resolveKeyframeValue(
                track.keyframes,
                timeline.currentFrame(),
              )
              if (val !== null && typeof val === 'number') return val
            }
          }
        }
        return fd.renderSettings.camera3D?.theta ?? 0
      case 'camera3D.phi':
        if (timeline.isDrivingView()) {
          if (
            timeline.hasKeyframeAtFrame('camera3D.phi', timeline.currentFrame())
          ) {
            const track = timeline
              .tracks()
              .find((t) => t.parameterPath === 'camera3D.phi')
            if (track) {
              const val = resolveKeyframeValue(
                track.keyframes,
                timeline.currentFrame(),
              )
              if (val !== null && typeof val === 'number') return val
            }
          }
        }
        return fd.renderSettings.camera3D?.phi ?? Math.PI / 2
      case 'camera3D.radius':
        if (timeline.isDrivingView()) {
          if (
            timeline.hasKeyframeAtFrame(
              'camera3D.radius',
              timeline.currentFrame(),
            )
          ) {
            const track = timeline
              .tracks()
              .find((t) => t.parameterPath === 'camera3D.radius')
            if (track) {
              const val = resolveKeyframeValue(
                track.keyframes,
                timeline.currentFrame(),
              )
              if (val !== null && typeof val === 'number') return val
            }
          }
        }
        return fd.renderSettings.camera3D?.radius ?? 5
      case 'camera3D.fov':
        if (timeline.isDrivingView()) {
          if (
            timeline.hasKeyframeAtFrame('camera3D.fov', timeline.currentFrame())
          ) {
            const track = timeline
              .tracks()
              .find((t) => t.parameterPath === 'camera3D.fov')
            if (track) {
              const val = resolveKeyframeValue(
                track.keyframes,
                timeline.currentFrame(),
              )
              if (val !== null && typeof val === 'number') return val
            }
          }
        }
        return fd.renderSettings.camera3D?.fov ?? 60
      case 'blendWeight':
        return blendWeight()
      default:
        break
    }
    // Handle transform paths: transform.{tid}.{prop} or transform.{tid}.{sub}.{key}
    const parts = path.split('.')
    if (parts[0] === 'transform') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const transforms = fd.transforms as Record<string, any>
      if (parts.length === 3 && parts[2] === 'probability') {
        return transforms[parts[1]!]?.probability ?? null
      }
      if (parts.length === 3 && parts[2] === 'colorSpeed') {
        return transforms[parts[1]!]?.colorSpeed ?? 0.4
      }
      if (
        parts.length === 4 &&
        (parts[2] === 'preAffine' || parts[2] === 'postAffine')
      ) {
        const affine = transforms[parts[1]!]?.[parts[2]]
        if (affine && parts[3]! in affine) {
          return affine[parts[3]!] as number
        }
      }
      if (parts.length === 4 && parts[2] === 'color') {
        const color = transforms[parts[1]!]?.color
        if (color && parts[3]! in color) {
          return color[parts[3]!]
        }
      }
      return null
    }
    // Handle transform variation parameter: {transformId}.{variationId}.{paramName}
    if (parts.length === 3) {
      const [transformId, variationId, paramName] = parts as [
        string,
        string,
        string,
      ]
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const transform = (fd.transforms as Record<string, any>)[transformId] as
        | {
            variations?: Record<
              string,
              { type: string; params?: Record<string, number> }
            >
          }
        | undefined
      const variation = transform?.variations?.[variationId]
      if (variation) {
        if (variation.params) {
          const val = variation.params[paramName]
          if (val !== undefined) return val
        } else if (isAnyParametricVariationType(variation.type)) {
          // Params not initialized yet — fall back to defaults
          const vType = variation.type
          const vDef = (allTransformVariations as Record<string, unknown>)[
            vType
          ] as { paramDefaults: Record<string, number> } | undefined
          if (vDef && paramName in vDef.paramDefaults) {
            const d = vDef.paramDefaults[paramName]
            if (d !== undefined) return d
          }
        }
      }
    }
    // Handle transform variation weight: {transformId}.{variationId}
    if (
      parts.length === 2 &&
      parts[0] !== 'transform' &&
      parts[0] !== 'camera'
    ) {
      const [transformId, variationId] = parts as [string, string]
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const variation = (fd.transforms as Record<string, any>)[transformId]
        ?.variations?.[variationId] as { weight?: number } | undefined
      if (variation?.weight !== undefined) return variation.weight
    }
    return null
  }
  timeline.setValueResolver(getFlameValue)

  function setFlameValue(
    path: string,
    value:
      | number
      | string
      | [number, number, number]
      | [number, number, number, number],
  ) {
    // Silent: this is the timeline's write-through (recording, playback
    // holds, and undo/redo write-back). The timeline undo stack owns these
    // changes — recording them in flame history double-counted every
    // preset keyframe at the current frame and would turn timeline undo
    // write-backs into fresh flame entries.
    history.setSilently((draft) => {
      switch (path) {
        case 'blendWeight':
          draft.renderSettings.blendWeight = value as number
          break
        case 'exposure':
          draft.renderSettings.exposure = value as number
          break
        case 'skipIters':
          draft.renderSettings.skipIters = value as number
          break
        case 'plotsPerChain':
          draft.renderSettings.plotsPerChain = value as number
          break
        case 'vibrancy':
          draft.renderSettings.vibrancy = value as number
          break
        case 'contrast':
          draft.renderSettings.contrast = value as number
          break
        case 'gamma':
          draft.renderSettings.gamma = value as number
          break
        case 'highlightPower':
          draft.renderSettings.highlightPower = value as number
          break
        case 'drawMode':
          draft.renderSettings.drawMode = value as 'light' | 'paint'
          break
        case 'colorInitMode':
          draft.renderSettings.colorInitMode = value as
            | 'colorInitZero'
            | 'colorInitPosition'
          break
        case 'pointInitMode':
          draft.renderSettings.pointInitMode = value as PointInitMode
          break
        case 'densityEstimationQuality':
          draft.renderSettings.densityEstimationQuality = value as number
          break
        case 'estimatorCurve':
          draft.renderSettings.estimatorCurve = value as number
          break
        case 'paletteMode':
          draft.renderSettings.paletteMode = value as number
          break
        case 'palettePhase':
          draft.renderSettings.palettePhase = value as number
          break
        case 'paletteSpeed':
          draft.renderSettings.paletteSpeed = value as number
          break
        case 'backgroundColor':
          if (Array.isArray(value)) {
            draft.renderSettings.backgroundColor = value as [
              number,
              number,
              number,
            ]
          }
          break
        case 'edgeFadeColor':
          if (Array.isArray(value)) {
            draft.renderSettings.edgeFadeColor = value as [
              number,
              number,
              number,
              number,
            ]
          }
          break
        case 'camera.x':
          if (draft.renderSettings.camera) {
            draft.renderSettings.camera.position[0] = value as number
          }
          break
        case 'camera.y':
          if (draft.renderSettings.camera) {
            draft.renderSettings.camera.position[1] = value as number
          }
          break
        case 'camera.zoom':
          if (draft.renderSettings.camera) {
            draft.renderSettings.camera.zoom = value as number
          }
          break
        case 'camera.rotation':
          ;(draft.renderSettings.camera as
            | Record<string, unknown>
            | undefined)!.rotation = value
          break
        case 'camera3D.theta':
          if (draft.renderSettings.camera3D) {
            draft.renderSettings.camera3D.theta = value as number
          }
          break
        case 'camera3D.phi':
          if (draft.renderSettings.camera3D) {
            draft.renderSettings.camera3D.phi = value as number
          }
          break
        case 'camera3D.radius':
          if (draft.renderSettings.camera3D) {
            draft.renderSettings.camera3D.radius = value as number
          }
          break
        case 'camera3D.fov':
          if (draft.renderSettings.camera3D) {
            draft.renderSettings.camera3D.fov = value as number
          }
          break
        default: {
          const parts = path.split('.')
          if (parts[0] === 'transform') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const transforms = draft.transforms as Record<string, any>
            if (parts.length === 3 && parts[2] === 'probability') {
              if (transforms[parts[1]!]) {
                transforms[parts[1]!].probability = value as number
              }
            } else if (parts.length === 3 && parts[2] === 'colorSpeed') {
              if (transforms[parts[1]!]) {
                transforms[parts[1]!].colorSpeed = value as number
              }
            } else if (
              parts.length === 4 &&
              (parts[2] === 'preAffine' || parts[2] === 'postAffine')
            ) {
              const affine = transforms[parts[1]!]?.[parts[2]]
              if (affine && parts[3]! in affine) {
                affine[parts[3]!] = value as number
              }
            } else if (parts.length === 4 && parts[2] === 'color') {
              const color = transforms[parts[1]!]?.color
              if (color && parts[3]! in color) {
                color[parts[3]!] = value as number
              }
            }
          } else if (parts.length === 3) {
            const [transformId, variationId, paramName] = parts as [
              string,
              string,
              string,
            ]
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const transform = (draft.transforms as Record<string, any>)[
              transformId
            ] as
              | {
                  variations?: Record<
                    string,
                    { type: string; params?: Record<string, number> }
                  >
                }
              | undefined
            const variation = transform?.variations?.[variationId]
            if (variation?.params) {
              variation.params[paramName] = value as number
            }
          } else if (parts.length === 2 && parts[0] !== 'camera') {
            const [transformId, variationId] = parts as [string, string]
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const transform = (draft.transforms as Record<string, any>)[
              transformId
            ] as
              | {
                  variations?: Record<string, { weight?: number }>
                }
              | undefined
            const variation = transform?.variations?.[variationId]
            if (variation) {
              variation.weight = value as number
            }
          }
          break
        }
      }
    })
  }
  timeline.setValueWriter(setFlameValue)

  // Effective camera values: read from timeline whenever animation is enabled
  // so the camera follows keyframes during playback, seeking, and when stopped.
  const animatingCamera = () => timeline.isDrivingView()

  const effectiveZoom = createMemo(() => {
    if (animatingCamera()) {
      const val = timeline.resolveValueAtPath(
        'camera.zoom',
        timeline.currentFrame(),
      )
      if (val !== null && typeof val === 'number') return val
    }
    return flameDescriptor.renderSettings.camera.zoom
  })

  const effectivePosition = createMemo(() => {
    const base = flameDescriptor.renderSettings.camera.position
    if (animatingCamera()) {
      const frame = timeline.currentFrame()
      // Resolve each axis independently — presets like Pan Left only keyframe
      // one axis, so requiring both tracks would freeze the camera.
      const xVal = timeline.resolveValueAtPath('camera.x', frame)
      const yVal = timeline.resolveValueAtPath('camera.y', frame)
      const x = typeof xVal === 'number' ? xVal : base[0]
      const y = typeof yVal === 'number' ? yVal : base[1]
      return vec2f(x, y)
    }
    return vec2f(...base)
  })

  useKeyboardShortcuts({
    Escape: () => {
      if (sidebarDiffView()) {
        closeSidebarDiff()
        return true
      }
      // Let browser/dialog handle Escape when no sidebar diff is open
    },
    KeyF: () => {
      if ('startViewTransition' in document) {
        document.startViewTransition(() => {
          setShowSidebar((p) => !p)
        })
      } else {
        setShowSidebar((p) => !p)
      }
      return true
    },
    KeyZ: (ev) => {
      if (animationExportRunning()) return false
      if (ev.metaKey || ev.ctrlKey) {
        // Chronological across flame history + timeline (see undoRouting.ts);
        // the toolbar Undo/Redo buttons route through the same arbiter.
        return ev.shiftKey ? undoRouter.redoLast() : undoRouter.undoLast()
      }
    },
    KeyY: (ev) => {
      if (animationExportRunning()) return false
      if (ev.metaKey || ev.ctrlKey) {
        return undoRouter.redoLast()
      }
    },
    KeyD: (ev) => {
      // Plain "D" pans the 3D camera right (WASD), so the theme toggle lives
      // on Ctrl/Cmd+D to avoid the conflict.
      if (!(ev.ctrlKey || ev.metaKey)) return false
      if (animationExportRunning()) return false
      const toggleTheme = () => {
        setTheme(theme() === 'dark' ? 'light' : 'dark')
      }
      if ('startViewTransition' in document) {
        document.startViewTransition(toggleTheme)
      } else {
        toggleTheme()
      }
      return true
    },
    KeyI: (ev) => {
      if (animationExportRunning()) return false
      if (ev.altKey) {
        const path = targetedParameter()
        if (path) {
          timeline.removeKeyframe(path, timeline.currentFrame())
        }
      } else {
        const path = targetedParameter()
        if (path) {
          timeline.addKeyframeAtCurrentFrame(path)
        }
      }
      return true
    },
    Space: () => {
      if (animationExportRunning()) return false
      if (!showTimeline()) return
      if (!animationEnabled()) {
        setAnimationEnabled(true)
      }
      timeline.togglePlay()
      return true
    },
  })

  const timelineDuration = () => timeline.config().endFrame
  const setTimelineDuration: Setter<number> = (value) => {
    const newDuration =
      typeof value === 'function' ? value(timeline.config().endFrame) : value
    timeline.updateConfigUndoable({ endFrame: newDuration })
    return newDuration
  }

  // Command context: bridges registered commands to app signals
  const cmdContext: CommandContext = {
    flameDescriptor: () => flameDescriptor,
    setFlameDescriptor,
    blendFlame,
    setBlendFlame,
    blendWeight,
    setBlendWeight,
    pixelRatio,
    setPixelRatio,
    zoom: effectiveZoom,
    setZoom: setFlameZoom,
    position: effectivePosition,
    setPosition: setFlamePosition,
    sidebar: {
      open: showSidebar,
      setOpen: setShowSidebar,
    },
    timeline: {
      tracks: timeline.tracks,
      setTracks: timeline.setTracks,
      animationEnabled,
      setAnimationEnabled,
      duration: timelineDuration,
      setDuration: setTimelineDuration,
      currentFrame: timeline.currentFrame,
      setCurrentFrame: timeline.setCurrentFrame,
      play: timeline.play,
      setLoop: (loop) => {
        timeline.updateConfigUndoable({ loop })
      },
      setFps: (fps) => {
        timeline.updateConfigUndoable({ fps })
      },
      addKeyframe: (path, frame, value, easing) => {
        timeline.addKeyframe(
          path,
          frame,
          value,
          easing as EasingCurve | undefined,
        )
      },
    },
    camera: {
      center: () => {
        setFlameZoom(1)
        setFlamePosition(vec2f(0, 0))
      },
    },
    modal: {
      open: (name: string) => {
        if (name === 'exportPng') void showExportPngDialog()
        if (name === 'exportAnimation') void showExportPngDialog('animation')
      },
    },
  }
  useShortcutManager(cmdContext)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  runTourCommand.fn = (id, ...args: any[]) => {
    executeCommand(id, cmdContext, ...args)
  }

  const startTimelineDrag = createDragHandler((initEvent) => {
    const handle = initEvent.currentTarget as HTMLElement
    const container = handle.parentElement
    if (!container) return
    const startY = initEvent.clientY
    const startHeight = container.offsetHeight

    function setHeight(px: number) {
      // Cap matches the CSS max-height (55vh desktop, 45vh on mobile) so the
      // handle and the rendered panel height stay in sync.
      const maxPx = window.innerHeight * (isMobile() ? 0.45 : 0.55)
      const clamped = Math.max(100, Math.min(maxPx, px))
      container!.style.setProperty('--timeline-height', `${clamped}px`)
    }

    return {
      onPointerMove(event) {
        const dy = startY - event.clientY
        setHeight(startHeight + dy)
      },
    }
  })

  const startSidebarDrag = createDragHandler((_initEvent) => {
    const sidebar = sidebarRef
    if (!sidebar) return

    return {
      onPointerMove() {
        setSidebarWidth()
      },
    }
  })

  return (
    <ChangeHistoryContextProvider value={history}>
      <TimelineContextProvider value={timeline}>
        <Dropzone class={ui.layout} onDrop={onDrop}>
          <>
            <div
              class={ui.canvasContainer}
              data-tour-target="canvas"
              classList={{ [ui.fullscreen as string]: !showSidebar() }}
              onClick={() => {
                // Tap canvas to close sidebar on mobile
                if (isMobile() && !sidebarHidden()) setSidebarHidden(true)
              }}
            >
              <Show when={isMobile()}>
                <button
                  class={ui.sidebarToggle}
                  onClick={(e) => {
                    e.stopPropagation()
                    setSidebarHidden((p) => !p)
                  }}
                  aria-label="Toggle sidebar"
                >
                  <Menu />
                </button>
              </Show>
              {/* Text alternative for the WebGPU canvas (WCAG 1.1.1): a name
                  via aria-label plus a live, screen-reader-only description of
                  the current flame (a pixel-accurate alt is impossible for
                  generative art, so describe its structure instead). */}
              <p id="flame-canvas-desc" class="sr-only" aria-live="polite">
                {(() => {
                  const name = flameDescriptor.metadata?.name?.trim()
                  const count = Object.keys(
                    flameDescriptor.transforms ?? {},
                  ).length
                  const label =
                    name && name.toLowerCase() !== 'unknown'
                      ? name
                      : 'Untitled flame'
                  return `${label}: ${count} transform${count === 1 ? '' : 's'}.`
                })()}
              </p>
              <AutoCanvas
                class={ui.canvas}
                role="img"
                ariaLabel="Fractal flame preview"
                ariaDescribedby="flame-canvas-desc"
                pixelRatio={canvasPixelRatio()}
                fixedResolution={exportDimensions()}
              >
                <Suspense>
                  <ErrorBoundary
                    fallback={(err) => (
                      <div
                        style={{
                          color: 'red',
                          display: 'flex',
                          'align-items': 'center',
                          'justify-content': 'center',
                          height: '100%',
                          'text-align': 'center',
                          padding: '20px',
                          'flex-direction': 'column',
                          gap: '1rem',
                        }}
                      >
                        <p>
                          Failed to render flame. The flame or animation data
                          might be invalid or incompatible.
                        </p>
                        <p>
                          <code>{String(err)}</code>
                        </p>
                        <Button
                          onClick={() => {
                            window.location.reload()
                          }}
                        >
                          Reload Page
                        </Button>
                      </div>
                    )}
                  >
                    <Show
                      when={effectiveFlame().renderSettings.dimensions === 3}
                      fallback={
                        <WheelZoomCamera2D
                          zoom={[effectiveZoom, setFlameZoom]}
                          position={[effectivePosition, setFlamePosition]}
                          interactive={() =>
                            !timeline.isPlaying() &&
                            (!animationExportRunning() ||
                              cameraDuringExportEnabled())
                          }
                        >
                          <Flam3
                            quality={
                              exportQuality() ?? qualityPresets[qualityPreset()]
                            }
                            pointCountPerBatch={DEFAULT_POINT_COUNT}
                            isExportRenderer
                            adaptiveFilterEnabled={adaptiveFilterEnabled()}
                            stochasticFilterEnabled={stochasticFilterEnabled()}
                            animationEnabled={animationEnabled()}
                            flameDescriptor={effectiveFlame()}
                            renderInterval={finalRenderInterval()}
                            onExportImage={onExportImage()}
                            edgeFadeColor={
                              showSidebar()
                                ? EDGE_FADE_COLOR[theme()]
                                : vec4f(0)
                            }
                            setCurrentQuality={(fn) =>
                              setCurrentQuality(() => fn)
                            }
                            setQualityPointCountLimit={(fn) =>
                              setQualityPointCountLimit(() => fn)
                            }
                            palette={() => selectedPalette()}
                            blendFlame={blendFlame()}
                            blendWeight={resolvedBlendWeight()}
                          />
                        </WheelZoomCamera2D>
                      }
                    >
                      <WheelZoomCamera3D
                        theta={[effectiveTheta, setFlameTheta]}
                        phi={[effectivePhi, setFlamePhi]}
                        radius={[effectiveRadius, setFlameRadius]}
                        target={[effectiveTarget3D, setFlameTarget3D]}
                        fov={[effectiveFov, setFlameFov]}
                        roll={[effectiveRoll, setFlameRoll]}
                        flyMode={flyMode}
                        flySpeed={flySpeed}
                        interactive={() =>
                          !timeline.isPlaying() &&
                          (!animationExportRunning() ||
                            cameraDuringExportEnabled())
                        }
                      >
                        <Flam3
                          quality={
                            exportQuality() ?? qualityPresets[qualityPreset()]
                          }
                          pointCountPerBatch={DEFAULT_POINT_COUNT}
                          isExportRenderer
                          adaptiveFilterEnabled={adaptiveFilterEnabled()}
                          animationEnabled={animationEnabled()}
                          flameDescriptor={effectiveFlame()}
                          renderInterval={finalRenderInterval()}
                          onExportImage={onExportImage()}
                          edgeFadeColor={
                            showSidebar() ? EDGE_FADE_COLOR[theme()] : vec4f(0)
                          }
                          setCurrentQuality={(fn) =>
                            setCurrentQuality(() => fn)
                          }
                          setQualityPointCountLimit={(fn) =>
                            setQualityPointCountLimit(() => fn)
                          }
                          palette={() => selectedPalette()}
                          blendFlame={blendFlame()}
                          blendWeight={resolvedBlendWeight()}
                        />
                      </WheelZoomCamera3D>
                    </Show>
                  </ErrorBoundary>
                </Suspense>
              </AutoCanvas>
              <Show when={hoveredVariationType()} keyed>
                {(hv) => (
                  <div class={ui.hoverPreviewBadge}>
                    Previewing: {getNormalizedVariationName(hv)}
                  </div>
                )}
              </Show>
              <Show when={hoveredCustomVarDef()} keyed>
                {(cv) => (
                  <div class={ui.hoverPreviewBadge}>
                    Previewing custom: {cv.name}
                  </div>
                )}
              </Show>
              <Show when={hoveredBlendName()} keyed>
                {(name) => (
                  <div class={ui.hoverPreviewBadge}>Blending with {name}</div>
                )}
              </Show>
              <Show when={toastMessage()}>
                {(msg) => <div class={ui.toast}>{msg()}</div>}
              </Show>
              <ProgressBar />
              <ExportJobHost />
              <ExportJobTracker />
              <div class={ui.bottomBar}>
                <Show when={effectiveFlame().renderSettings.dimensions === 3}>
                  <OrientationGizmo
                    theta={[effectiveTheta, setFlameTheta]}
                    phi={[effectivePhi, setFlamePhi]}
                  />
                </Show>
                <div
                  class={ui.viewControlsWrapper}
                  data-tour-target="view-controls"
                  style={{
                    'pointer-events':
                      animationExportRunning() || timeline.isPlaying()
                        ? 'none'
                        : 'auto',
                    opacity:
                      animationExportRunning() || timeline.isPlaying()
                        ? 0.5
                        : 1,
                  }}
                >
                  <ViewControls
                    zoom={effectiveZoom()}
                    setZoom={setFlameZoom}
                    position={effectivePosition()}
                    setPosition={setFlamePosition}
                    pixelRatio={pixelRatio()}
                    setPixelRatio={setPixelRatio}
                    controlsDisabled={timeline.isPlaying()}
                    onUndo={undoRouter.undoLast}
                    onRedo={undoRouter.redoLast}
                    canUndo={undoRouter.canUndo}
                    canRedo={undoRouter.canRedo}
                    blendFlame={blendFlame()}
                    blendWeight={resolvedBlendWeight()}
                    onPickBlendFlame={pickBlendFlame}
                    onMorphFlame={pickMorphFlame}
                    onBreedFlame={pickBreedFlame}
                    onEvolveFlame={pickEvolveFlame}
                    onSimulatorFlame={pickSimulatorFlame}
                    onDiffFlame={pickDiffFlame}
                    onAncestryFlame={pickAncestryFlame}
                    onGalleryFlame={pickGalleryFlame}
                    onClearBlendFlame={() => {
                      setBlendFlame(undefined)
                    }}
                    onBlendWeightChange={setBlendWeight}
                    is3D={effectiveFlame().renderSettings.dimensions === 3}
                    flameName={flameDescriptor.metadata?.name}
                    theta={effectiveTheta()}
                    phi={effectivePhi()}
                    radius={effectiveRadius()}
                    fov={effectiveFov()}
                    setTheta={setFlameTheta}
                    setPhi={setFlamePhi}
                    setRadius={setFlameRadius}
                    setFov={setFlameFov}
                    flyMode={flyMode()}
                    flySpeed={flySpeed[0]()}
                    setFlySpeed={flySpeed[1]}
                    onAudioReactive={() => {
                      setShowBlendGallery(false)
                      setShowSonificationPanel(false)
                      setShowAudioPanel(true)
                    }}
                    onSonification={() => {
                      setShowBlendGallery(false)
                      setShowAudioPanel(false)
                      setShowSonificationPanel(true)
                    }}
                  />
                </div>
                <Show when={showTimeline()}>
                  <div
                    class={ui.timelineContainer}
                    // During playback (and animation export) the timeline is
                    // dimmed + locked so the canvas/animation reads cleanly.
                    // Playback additionally tags itself so ONLY the transport bar
                    // stays clickable (to pause) — see [data-playback-locked] in
                    // TimelineSection.module.css. Animation export stays fully
                    // locked so a stray click can't start playback mid-render
                    // (#8). Image export now runs offscreen (background job) and
                    // does NOT lock the workspace.
                    data-playback-locked={
                      timeline.isPlaying() && !animationExportRunning()
                        ? 'true'
                        : undefined
                    }
                    style={{
                      'pointer-events':
                        animationExportRunning() || timeline.isPlaying()
                          ? 'none'
                          : 'auto',
                      opacity:
                        animationExportRunning() || timeline.isPlaying()
                          ? 0.5
                          : 1,
                    }}
                    onWheel={(ev) => {
                      if (!ev.ctrlKey && !ev.metaKey) return
                      ev.preventDefault()
                      const delta = -ev.deltaY * 0.5
                      const container = ev.currentTarget as HTMLElement
                      const currentHeight = container.offsetHeight
                      const newHeight = Math.max(
                        100,
                        Math.min(
                          window.innerHeight * (isMobile() ? 0.45 : 0.55),
                          currentHeight + delta,
                        ),
                      )
                      container.style.setProperty(
                        '--timeline-height',
                        `${newHeight}px`,
                      )
                    }}
                  >
                    <div
                      class={ui.timelineResizeHandle}
                      onPointerDown={startTimelineDrag}
                      title="Resize timeline"
                    />
                    <TimelineSection
                      formatTrackLabel={readableIds().formatTrackPath}
                      flameDescriptor={flameDescriptor}
                      onOpenAnimationGenerator={openAnimationGenerator}
                    />
                  </div>
                </Show>
              </div>
            </div>
          </>
          <DebugOverlay
            animationEnabled={animationEnabled()}
            flameDescriptor={flameDescriptor}
          />

          <Show when={showSidebar()}>
            <div
              class={ui.sidebar}
              classList={{
                [ui.sidebarLocked as string]: timeline.isPlaying(),
                [ui.sidebarHidden as string]: sidebarHidden(),
              }}
              style={{ '--sidebar-width': `${sidebarWidth()}rem` }}
              data-tour-target="sidebar"
              ref={(el) => {
                sidebarRef = el
                setSidebarEl(el)
              }}
            >
              {SIDEBAR_RESIZABLE && (
                <div
                  class={ui.sidebarResizeHandle}
                  onPointerDown={startSidebarDrag}
                />
              )}
              <Show when={timeline.isPlaying() || animationExportRunning()}>
                <div
                  class={ui.playbackOverlay}
                  classList={{
                    [ui.exportOverlay as string]: animationExportRunning(),
                  }}
                  onClick={() => {
                    if (timeline.isPlaying()) {
                      timeline.togglePlay()
                    }
                  }}
                >
                  <span class={ui.playbackOverlayText}>
                    {animationExportRunning()
                      ? 'Rendering animation...'
                      : 'Tap to stop animation'}
                  </span>
                  <Show when={animationExportRunning()}>
                    {/* Stop the click from bubbling to the overlay's
                        tap-to-stop-playback handler. */}
                    <div
                      onClick={(e) => {
                        e.stopPropagation()
                      }}
                    >
                      <ExportActions
                        variant="overlay"
                        stopLabel="Stop & Save"
                        stopTitle="Stop after current frame and save"
                        onStop={() => setForceAnimationExportNow(true)}
                        cancelTitle="Cancel and discard"
                        onCancel={() => {
                          const cancelFn = animationExportCancel()
                          if (cancelFn) cancelFn()
                        }}
                      />
                    </div>
                  </Show>
                </div>
              </Show>
              <Show when={isMobile()}>
                <div class={ui.sidebarCloseRow}>
                  <button
                    class={ui.sidebarCloseBtn}
                    onClick={() => setSidebarHidden(true)}
                    aria-label="Collapse sidebar"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path
                        fill="currentColor"
                        d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"
                      />
                    </svg>
                  </button>
                </div>
              </Show>
              <div class={ui.sidebarScroll} ref={sidebarScrollRef}>
                <Show
                  when={sidebarDiffView()}
                  keyed
                  fallback={
                    <Show
                      when={
                        showBlendGallery() ||
                        showAudioPanel() ||
                        showSonificationPanel()
                      }
                      fallback={
                        <>
                          <Show when={quickPickState()} keyed>
                            {(state) => (
                              <QuickVariationPicker
                                currentType={
                                  flameDescriptor.transforms[state.tid]
                                    ?.variations[state.vid]?.type ?? state.type
                                }
                                dims={
                                  (flameDescriptor.renderSettings.dimensions ??
                                    2) as Dims
                                }
                                hardwareTier={props.hardwareTier}
                                pointInitMode={
                                  flameDescriptor.renderSettings.pointInitMode
                                }
                                onSelect={(newType) => {
                                  setFlameDescriptor((draft) => {
                                    const existingVar =
                                      draft.transforms[state.tid]?.variations[
                                        state.vid
                                      ]
                                    if (existingVar) {
                                      draft.transforms[state.tid]!.variations[
                                        state.vid
                                      ] = deepClone(
                                        getVariationDefault(
                                          newType,
                                          existingVar.weight,
                                        ),
                                      )
                                    }
                                  })
                                }}
                                onClose={() => {
                                  // Save scroll position before the Show block unmounts
                                  savedScrollTop =
                                    sidebarScrollRef?.scrollTop ?? 0
                                  setHoveredVariationType(null)
                                  setQuickPickState(null)
                                  // Restore after Solid re-renders the normal sidebar
                                  queueMicrotask(() => {
                                    if (sidebarScrollRef) {
                                      sidebarScrollRef.scrollTop =
                                        savedScrollTop
                                    }
                                  })
                                }}
                                onHoverType={(type) =>
                                  setHoveredVariationType(type)
                                }
                                onHoverClear={() =>
                                  setHoveredVariationType(null)
                                }
                                mode={quickPickerMode()}
                                onModeChange={setQuickPickerMode}
                                onOpenFullSelector={() => {
                                  console.info(
                                    '[QuickVariationPicker] onOpenFullSelector — opening full VariationSelector',
                                    { tid: state.tid, vid: state.vid },
                                  )
                                  const currentVar =
                                    flameDescriptor.transforms[state.tid]
                                      ?.variations[state.vid]
                                  if (!currentVar) return
                                  // Close quick picker first so modal stacking works
                                  setQuickPickState(null)
                                  queueMicrotask(() => {
                                    showVariationSelector(
                                      deepClone(currentVar),
                                      deepClone(flameDescriptor),
                                      state.tid,
                                      state.vid,
                                      {
                                        setFlameTheta,
                                        setFlamePhi,
                                        setFlameRadius,
                                        setFlameTarget3D,
                                        setFlameFov,
                                      },
                                    )
                                      .then((newValue) => {
                                        if (
                                          newValue === undefined ||
                                          !isVariationType(
                                            newValue.variation.type,
                                          )
                                        ) {
                                          return
                                        }
                                        setFlameDescriptor((draft) => {
                                          draft.transforms[
                                            state.tid
                                          ]!.preAffine =
                                            newValue.transform.preAffine
                                          draft.transforms[
                                            state.tid
                                          ]!.variations[state.vid] =
                                            newValue.variation
                                        })
                                      })
                                      .catch((err: unknown) => {
                                        console.warn(
                                          'Cannot load this variation, reason: ',
                                          err,
                                        )
                                      })
                                  })
                                }}
                              />
                            )}
                          </Show>
                          <Show when={!quickPickState()}>
                            <CollapsibleCard title="Affine">
                              <AffineEditor
                                class={ui.affineEditor}
                                transforms={flameDescriptor.transforms}
                                setTransforms={(setFn) => {
                                  setFlameDescriptor((draft) => {
                                    setFn(draft.transforms)
                                  })
                                }}
                                finalTransform={
                                  flameDescriptor.finalTransform ??
                                  ((flameDescriptor.renderSettings.dimensions ??
                                    2) === 3
                                    ? // 3D identity in the kernel's layout
                                      // (diagonal a,f,k; translation d,h,l)
                                      {
                                        a: 1,
                                        b: 0,
                                        c: 0,
                                        d: 0,
                                        e: 0,
                                        f: 1,
                                        g: 0,
                                        h: 0,
                                        i: 0,
                                        j: 0,
                                        k: 1,
                                        l: 0,
                                      }
                                    : { a: 1, b: 0, c: 0, d: 0, e: 1, f: 0 })
                                }
                                setFinalTransform={(affine) => {
                                  setFlameDescriptor((draft) => {
                                    draft.finalTransform = affine
                                  })
                                }}
                                is3D={
                                  (flameDescriptor.renderSettings.dimensions ??
                                    2) === 3
                                }
                                selectedTransformId={selectedTransformId}
                                setSelectedTransformId={setSelectedTransformId}
                                enableChangeTracking
                              />
                            </CollapsibleCard>
                            <CollapsibleCard title="Color">
                              <div>
                                <ColorEditor
                                  transforms={flameDescriptor.transforms}
                                  setTransforms={(setFn) => {
                                    setFlameDescriptor((draft) => {
                                      setFn(draft.transforms)
                                    })
                                  }}
                                  selectedTransformId={selectedTransformId}
                                  setSelectedTransformId={
                                    setSelectedTransformId
                                  }
                                  enableChangeTracking
                                />
                              </div>
                            </CollapsibleCard>
                            <CollapsibleCard
                              title="Palette"
                              defaultOpen={false}
                            >
                              <PaletteSelector
                                selectedPaletteId={selectedPaletteId()}
                                onSelect={handlePaletteSelect}
                                onUnselect={handlePaletteUnselect}
                              />
                            </CollapsibleCard>
                            <Show
                              when={
                                flameDescriptor.renderSettings.dimensions !== 3
                              }
                            >
                              <CollapsibleCard
                                title="Custom Variations"
                                defaultOpen={false}
                              >
                                <For
                                  each={customVariationsList()}
                                  fallback={
                                    <div class={ui.customVarEmpty}>
                                      No custom variations yet
                                    </div>
                                  }
                                >
                                  {(def) => (
                                    <div
                                      class={ui.customVarItem}
                                      onContextMenu={(e) => {
                                        e.preventDefault()
                                      }}
                                      onMouseEnter={() =>
                                        setHoveredCustomVarDef(def)
                                      }
                                      onMouseLeave={() =>
                                        setHoveredCustomVarDef(null)
                                      }
                                      onClick={() => {
                                        void showCustomVariationEditor(
                                          def,
                                        ).then((addedDef) => {
                                          if (addedDef) {
                                            setFlameDescriptor((draft) => {
                                              addTransformWithVariation(
                                                draft,
                                                addedDef.id,
                                              )
                                            })
                                          }
                                          setCustomVarsVersion((v) => v + 1)
                                        })
                                      }}
                                    >
                                      <span class={ui.customVarItemName}>
                                        {def.name}
                                      </span>
                                      <div class={ui.customVarItemActions}>
                                        <button
                                          class={ui.customVarItemBtn}
                                          classList={{
                                            [ui.customVarItemBtnPrimary as string]: true,
                                          }}
                                          title="Add to flame"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            setHoveredCustomVarDef(null)
                                            setFlameDescriptor((draft) => {
                                              addTransformWithVariation(
                                                draft,
                                                def.id,
                                              )
                                            })
                                          }}
                                        >
                                          <BoxArrowRight />
                                        </button>
                                        <button
                                          class={ui.customVarItemBtn}
                                          title="Share variation link"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            setHoveredCustomVarDef(null)
                                            void showShareVariationLinkModal(
                                              def,
                                            )
                                          }}
                                        >
                                          <Share />
                                        </button>
                                        <button
                                          class={ui.customVarItemBtn}
                                          title="Duplicate"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            duplicateCustomVariation(def.id)
                                            setCustomVarsVersion((v) => v + 1)
                                          }}
                                        >
                                          ⧉
                                        </button>
                                        <button
                                          class={ui.customVarItemBtn}
                                          classList={{
                                            [ui.customVarItemBtnDanger as string]: true,
                                          }}
                                          title="Delete"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            void handleDeleteCustomVariation(
                                              def,
                                            )
                                          }}
                                        >
                                          ×
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </For>
                                <button
                                  class={ui.customVarsButton}
                                  onClick={async () => {
                                    const addedDef =
                                      await showCustomVariationEditor()
                                    if (addedDef) {
                                      setFlameDescriptor((draft) => {
                                        addTransformWithVariation(
                                          draft,
                                          addedDef.id,
                                        )
                                      })
                                    }
                                    setCustomVarsVersion((v) => v + 1)
                                  }}
                                  title="Create a new custom variation"
                                >
                                  <Plus />
                                  <span>Create Variation</span>
                                </button>
                              </CollapsibleCard>
                            </Show>
                            <div
                              ref={randomizerCardRef}
                              data-tour-target="randomizer-card"
                            >
                              <FlameRandomizerCard
                                flame={flameDescriptor}
                                open={randomizerOpen()}
                                onToggleOpen={() =>
                                  setRandomizerOpen((v) => !v)
                                }
                                expandAnimationEpoch={randomizerAnimEpoch()}
                                historyEntries={randomizerHistory()}
                                selectedTimestamp={selectedHistoryTimestamp()}
                                onGenerateFlame={handleGenerateFlame}
                                onMutateFlame={handleMutateFlame}
                                onLoadHistory={handleLoadHistory}
                                onClearHistory={handleClearHistory}
                                onRandomizeAnimation={handleRandomizeAnimation}
                                onSmartAnimation={handleSmartAnimation}
                                onUpdateRenderSettings={
                                  handleUpdateRenderSettings
                                }
                                onApplyCandidate={(flame) => {
                                  if (blendFlame())
                                    showToast(
                                      'Blend is still active — the loaded flame will look mixed',
                                      4000,
                                    )
                                  history.replace(
                                    deepClone(flame),
                                    'Apply Random Flame',
                                  )
                                }}
                                hardwareTier={props.hardwareTier}
                                isBusy={isRandomizing()}
                              />
                            </div>
                            <div class={ui.transformsToolbar}>
                              <span class={ui.transformsToolbarLabel}>
                                Transforms
                              </span>
                              <span
                                class={ui.transformHeaderAction}
                                role="button"
                                tabindex={0}
                                title={
                                  anyTransformOpen()
                                    ? 'Collapse all transform cards'
                                    : 'Expand all transform cards'
                                }
                                onClick={toggleCollapseAllTransforms}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    toggleCollapseAllTransforms()
                                  }
                                }}
                              >
                                <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  stroke-width="2"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                >
                                  {/* Chevrons-up = collapse all; chevrons-down = expand all */}
                                  <Show
                                    when={anyTransformOpen()}
                                    fallback={
                                      <>
                                        <polyline points="6 5 12 11 18 5" />
                                        <polyline points="6 13 12 19 18 13" />
                                      </>
                                    }
                                  >
                                    <polyline points="18 11 12 5 6 11" />
                                    <polyline points="18 19 12 13 6 19" />
                                  </Show>
                                </svg>
                              </span>
                            </div>
                            <For
                              each={sortedTransformEntries(
                                recordEntries(flameDescriptor.transforms),
                              ).filter(([tid]) => !tid.startsWith('_sym__'))}
                            >
                              {([tid, transform]) => (
                                <CollapsibleCard
                                  title={readableIds().transformLabel[tid]!}
                                  open={!collapsedTransforms().has(tid)}
                                  onToggleOpen={() => {
                                    toggleTransformCollapsed(tid)
                                  }}
                                  selected={selectedTransformId() === tid}
                                  dimmed={
                                    selectedTransformId() !== null &&
                                    selectedTransformId() !== tid
                                  }
                                  accentColor={handleColor(
                                    theme(),
                                    vec2f(transform.color.x, transform.color.y),
                                  )}
                                  onToggleSelect={() =>
                                    toggleSelectedTransform(tid)
                                  }
                                  headerActions={
                                    <>
                                      <Show when={!hideDiceButtons()}>
                                        <span
                                          class={ui.transformHeaderAction}
                                          role="button"
                                          tabindex={0}
                                          title="Randomize transform color"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            // Uniform OkLab hue at a vivid chroma so
                                            // every hue is equally likely (the old
                                            // random in x/y skewed reddish).
                                            const hue = random01() * 2 * Math.PI
                                            const chroma =
                                              0.25 + random01() * 0.15
                                            setFlameDescriptor((draft) => {
                                              draft.transforms[tid]!.color.x =
                                                chroma * Math.cos(hue)
                                              draft.transforms[tid]!.color.y =
                                                chroma * Math.sin(hue)
                                            })
                                          }}
                                        >
                                          <Shuffle />
                                        </span>
                                      </Show>
                                      <span
                                        class={ui.transformHeaderAction}
                                        role="button"
                                        tabindex={0}
                                        title={
                                          transform.visible
                                            ? 'Hide transform'
                                            : 'Show transform'
                                        }
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          setFlameDescriptor((draft) => {
                                            draft.transforms[tid]!.visible =
                                              !draft.transforms[tid]!.visible
                                          })
                                        }}
                                      >
                                        {transform.visible ? (
                                          <Eye />
                                        ) : (
                                          <EyeOff />
                                        )}
                                      </span>
                                      <span
                                        class={ui.transformHeaderAction}
                                        role="button"
                                        tabindex={0}
                                        title="Delete transform"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          setFlameDescriptor((draft) => {
                                            if (
                                              recordKeys(draft.transforms)
                                                .length === 1
                                            ) {
                                              draft.transforms[tid] = deepClone(
                                                newDefaultTransform(),
                                              )
                                            } else {
                                              delete draft.transforms[tid]
                                            }
                                          })
                                        }}
                                      >
                                        <Cross />
                                      </span>
                                    </>
                                  }
                                >
                                  <div class={ui.transformGrid}>
                                    <div
                                      data-tour-target="probability"
                                      classList={{
                                        [ui.transformGridRow as string]: true,
                                        [ui.transformGridFirstRow as string]: true,
                                      }}
                                      onClick={() => {
                                        setTargetedParameter(
                                          `transform.${tid}.probability`,
                                        )
                                      }}
                                    >
                                      <Slider
                                        class={ui.transformGridFirstRow}
                                        label="Probability"
                                        value={transform.probability}
                                        min={0}
                                        max={1}
                                        step={0.001}
                                        onInput={(probability) => {
                                          setFlameDescriptor((draft) => {
                                            draft.transforms[tid]!.probability =
                                              probability
                                          })
                                        }}
                                        formatValue={(value) =>
                                          formatPercent(
                                            value / totalProbability(),
                                          )
                                        }
                                        dataParameterPath={`transform.${tid}.probability`}
                                      />
                                    </div>
                                    <div
                                      classList={{
                                        [ui.transformGridRow as string]: true,
                                      }}
                                      onClick={() => {
                                        setTargetedParameter(
                                          `transform.${tid}.colorSpeed`,
                                        )
                                      }}
                                    >
                                      <Slider
                                        class={ui.transformGridFirstRow}
                                        label="Color Speed"
                                        value={transform.colorSpeed ?? 0.4}
                                        min={0}
                                        max={1}
                                        step={0.01}
                                        onInput={(val) => {
                                          setFlameDescriptor((draft) => {
                                            draft.transforms[tid]!.colorSpeed =
                                              val
                                          })
                                        }}
                                        dataParameterPath={`transform.${tid}.colorSpeed`}
                                        data-tour-target="colorSpeed-slider"
                                      />
                                    </div>
                                    <For
                                      each={recordEntries(transform.variations)}
                                    >
                                      {([vid, variation]) => (
                                        <>
                                          <div class={ui.transformGridRow}>
                                            <button
                                              class={ui.variationButton}
                                              data-tour-target="variation-type"
                                              value={variation.type}
                                              title={
                                                customStatus(variation.type) ===
                                                'unavailable'
                                                  ? `${getNormalizedVariationName(variation.type)} — custom variation unavailable (deleted from your library)`
                                                  : getNormalizedVariationName(
                                                      variation.type,
                                                    )
                                              }
                                              onClick={() => {
                                                // Auto-open sidebar on mobile so the picker is visible
                                                if (
                                                  isMobile() &&
                                                  sidebarHidden()
                                                ) {
                                                  setSidebarHidden(false)
                                                }
                                                setQuickPickState({
                                                  tid: toTransformId(tid),
                                                  vid: toVariationId(vid),
                                                  type: variation.type,
                                                })
                                              }}
                                              onContextMenu={(e) => {
                                                e.preventDefault()
                                                showVariationSelector(
                                                  deepClone(variation),
                                                  deepClone(flameDescriptor),
                                                  toTransformId(tid),
                                                  toVariationId(vid),
                                                  {
                                                    setFlameTheta,
                                                    setFlamePhi,
                                                    setFlameRadius,
                                                    setFlameTarget3D,
                                                    setFlameFov,
                                                  },
                                                )
                                                  .then((newValue) => {
                                                    if (
                                                      newValue === undefined ||
                                                      !isVariationType(
                                                        newValue.variation.type,
                                                      )
                                                    ) {
                                                      return
                                                    }
                                                    setFlameDescriptor(
                                                      (draft) => {
                                                        draft.transforms[
                                                          tid
                                                        ]!.preAffine =
                                                          newValue.transform.preAffine
                                                        draft.transforms[
                                                          tid
                                                        ]!.variations[vid] =
                                                          newValue.variation
                                                      },
                                                    )
                                                  })
                                                  .catch((err: unknown) => {
                                                    console.warn(
                                                      'Cannot load this variation, reason: ',
                                                      err,
                                                    )
                                                  })
                                              }}
                                            >
                                              <div
                                                class={ui.variationButtonText}
                                              >
                                                <Show when={animationEnabled()}>
                                                  <span class={ui.readableId}>
                                                    {
                                                      readableIds()
                                                        .variationLabel[vid]
                                                    }
                                                  </span>
                                                </Show>
                                                <span class={ui.variationName}>
                                                  {getNormalizedVariationName(
                                                    variation.type,
                                                  )}
                                                </span>
                                              </div>
                                              {/* Custom variation marker: accent dot
                                              when live, red when the flame still
                                              references one deleted from the
                                              library. */}
                                              <Show
                                                when={
                                                  customStatus(
                                                    variation.type,
                                                  ) !== 'none'
                                                }
                                              >
                                                <span
                                                  class={ui.customBadge}
                                                  classList={{
                                                    [ui.customBadgeUnavailable as string]:
                                                      customStatus(
                                                        variation.type,
                                                      ) === 'unavailable',
                                                  }}
                                                  title={
                                                    customStatus(
                                                      variation.type,
                                                    ) === 'unavailable'
                                                      ? 'Custom variation — unavailable (deleted from your library)'
                                                      : `Custom Variation ${getNormalizedVariationName(variation.type)}`
                                                  }
                                                />
                                              </Show>
                                            </button>
                                            <div
                                              class={ui.sliderGridWrapper}
                                              classList={{
                                                [ui.parameterTarget as string]: true,
                                              }}
                                              data-tour-target="variation-weight"
                                              onClick={() => {
                                                setTargetedParameter(
                                                  `${tid}.${vid}`,
                                                )
                                              }}
                                            >
                                              <Slider
                                                value={variation.weight}
                                                min={0}
                                                max={1}
                                                step={0.001}
                                                dataParameterPath={`${tid}.${vid}`}
                                                onInput={(weight) => {
                                                  setFlameDescriptor(
                                                    (draft) => {
                                                      draft.transforms[
                                                        tid
                                                      ]!.variations[
                                                        vid
                                                      ]!.weight = weight
                                                    },
                                                  )
                                                }}
                                                formatValue={formatPercent}
                                              />
                                            </div>
                                            <Show when={!hideDiceButtons()}>
                                              <DiceButton
                                                onClick={() => {
                                                  setFlameDescriptor(
                                                    (draft) => {
                                                      const v =
                                                        draft.transforms[tid]!
                                                          .variations[vid]!
                                                      v.weight = random01()
                                                      const params =
                                                        randomizeVariationParams(
                                                          v.type,
                                                        )
                                                      if (params) {
                                                        ;(
                                                          v as {
                                                            params?: Record<
                                                              string,
                                                              number
                                                            >
                                                          }
                                                        ).params = params
                                                      }
                                                    },
                                                  )
                                                }}
                                                title="Randomize variation"
                                              />
                                            </Show>
                                            <button
                                              class={ui.visibilityButton}
                                              title={
                                                variation.visible
                                                  ? 'Hide variation'
                                                  : 'Show variation'
                                              }
                                              onClick={() => {
                                                setFlameDescriptor((draft) => {
                                                  const v =
                                                    draft.transforms[tid]!
                                                      .variations[vid]!
                                                  v.visible = !v.visible
                                                })
                                              }}
                                            >
                                              {variation.visible ? (
                                                <Eye />
                                              ) : (
                                                <EyeOff />
                                              )}
                                            </button>
                                            <button
                                              class={ui.deleteVariationButton}
                                              onClick={() => {
                                                setFlameDescriptor((draft) => {
                                                  if (
                                                    recordKeys(
                                                      draft.transforms[tid]!
                                                        .variations,
                                                    ).length === 1
                                                  ) {
                                                    draft.transforms[
                                                      tid
                                                    ]!.variations[vid] =
                                                      deepClone(
                                                        getVariationDefault(
                                                          variation.type,
                                                          1,
                                                        ),
                                                      )
                                                  } else {
                                                    delete draft.transforms[
                                                      tid
                                                    ]!.variations[vid]
                                                  }
                                                })
                                              }}
                                            >
                                              <Cross />
                                            </button>
                                          </div>
                                          <Show
                                            when={
                                              isAnyParametricVariationType(
                                                variation.type,
                                              ) && variation
                                            }
                                            keyed
                                          >
                                            {(variation) => (
                                              <div
                                                classList={{
                                                  [ui.transformGridRow as string]: true,
                                                  [ui.variationParamsRow as string]: true,
                                                  [ui.parameterTarget as string]: true,
                                                }}
                                                onClick={() => {
                                                  setTargetedParameter(
                                                    `${tid}.${vid}`,
                                                  )
                                                }}
                                              >
                                                <Dynamic
                                                  {...getParamsEditor(
                                                    variation,
                                                  )}
                                                  dataParameterPath={`${tid}.${vid}`}
                                                  setValue={(value) => {
                                                    setFlameDescriptor(
                                                      (draft) => {
                                                        const variationDraft =
                                                          draft.transforms[tid]
                                                            ?.variations[vid]
                                                        if (
                                                          variationDraft ===
                                                            undefined ||
                                                          !isAnyParametricVariationType(
                                                            variationDraft.type,
                                                          )
                                                        ) {
                                                          throw new Error(
                                                            `Unreachable code`,
                                                          )
                                                        }
                                                        ;(
                                                          variationDraft as {
                                                            params: Record<
                                                              string,
                                                              number
                                                            >
                                                          }
                                                        ).params =
                                                          value as Record<
                                                            string,
                                                            number
                                                          >
                                                      },
                                                    )
                                                  }}
                                                />
                                              </div>
                                            )}
                                          </Show>
                                        </>
                                      )}
                                    </For>

                                    <button
                                      class={ui.addTransformVariationButton}
                                      onClick={() => {
                                        setFlameDescriptor((draft) => {
                                          draft.transforms[tid]!.variations[
                                            generateVariationId()
                                          ] = deepClone(
                                            getVariationDefault(
                                              defaultLinearType(
                                                (flameDescriptor.renderSettings
                                                  .dimensions ?? 2) as Dims,
                                              ),
                                              1,
                                            ),
                                          )
                                        })
                                      }}
                                    >
                                      <Plus />
                                      Add variation
                                    </button>
                                  </div>
                                </CollapsibleCard>
                              )}
                            </For>
                            <Show
                              when={recordEntries(
                                flameDescriptor.transforms,
                              ).some(([tid]) => tid.startsWith('_sym__'))}
                            >
                              <CollapsibleCard
                                title={`Symmetry (${recordEntries(flameDescriptor.transforms).filter(([tid]) => tid.startsWith('_sym__')).length})`}
                                defaultOpen={true}
                              >
                                <div class={ui.symPanel}>
                                  <div class={ui.symControls}>
                                    <span class={ui.symControlsLabel}>
                                      Type
                                    </span>
                                    <select
                                      class={ui.select}
                                      value={currentSymType()}
                                      onChange={(e) => {
                                        applySymmetry(
                                          currentSymFolds(),
                                          e.currentTarget.value as
                                            | 'rotational'
                                            | 'dihedral',
                                        )
                                      }}
                                    >
                                      <option value="rotational">
                                        Rotational
                                      </option>
                                      <option value="dihedral">Dihedral</option>
                                    </select>
                                    <span class={ui.symControlsLabel}>
                                      Folds
                                    </span>
                                    <ScrubInput
                                      label=""
                                      value={currentSymFolds()}
                                      step={1}
                                      onInput={(val: number) => {
                                        const newN = Math.max(
                                          2,
                                          Math.round(val),
                                        )
                                        if (newN !== currentSymFolds()) {
                                          applySymmetry(newN, currentSymType())
                                        }
                                      }}
                                    />
                                  </div>

                                  <div class={ui.symGallery}>
                                    <For each={symTransformIds()}>
                                      {(tid) => {
                                        const transform = () =>
                                          flameDescriptor.transforms[tid]!
                                        const preAffine = () =>
                                          transform().preAffine
                                        const isReflection = () => {
                                          const a = preAffine()
                                          return (
                                            a.a === -1 &&
                                            a.d === 0 &&
                                            a.b === 0 &&
                                            a.e === 1
                                          )
                                        }
                                        const angle = () => {
                                          const a = preAffine()
                                          let v = Math.atan2(a.d, a.a)
                                          if (v < 0) v += 2 * Math.PI
                                          return v
                                        }
                                        return (
                                          <div
                                            class={ui.symItem}
                                            classList={{
                                              [ui.symItemHidden as string]:
                                                !transform().visible,
                                            }}
                                          >
                                            <span
                                              class={ui.symBadge}
                                              classList={{
                                                [ui.symBadgeReflection as string]:
                                                  isReflection(),
                                              }}
                                            >
                                              {
                                                readableIds().transformLabel[
                                                  tid
                                                ]
                                              }
                                            </span>
                                            <div class={ui.symAngle}>
                                              <Show
                                                when={!isReflection()}
                                                fallback={
                                                  <span
                                                    style={{
                                                      'font-size': '0.65rem',
                                                      color:
                                                        'var(--neutral-500)',
                                                      'white-space': 'nowrap',
                                                    }}
                                                  >
                                                    Reflection
                                                  </span>
                                                }
                                              >
                                                <AngleEditor
                                                  mode="inline"
                                                  value={angle()}
                                                  dataParameterPath={`transform.${tid}.preAffine.a`}
                                                  keyframePaths={[
                                                    `transform.${tid}.preAffine.a`,
                                                    `transform.${tid}.preAffine.b`,
                                                    `transform.${tid}.preAffine.d`,
                                                    `transform.${tid}.preAffine.e`,
                                                  ]}
                                                  setValue={(newAngle) => {
                                                    const cos =
                                                      Math.cos(newAngle)
                                                    const sin =
                                                      Math.sin(newAngle)
                                                    setFlameDescriptor(
                                                      (draft) => {
                                                        const t =
                                                          draft.transforms[tid]
                                                        if (t) {
                                                          t.preAffine = {
                                                            a: cos,
                                                            b: -sin,
                                                            c: 0,
                                                            d: sin,
                                                            e: cos,
                                                            f: 0,
                                                          }
                                                        }
                                                      },
                                                    )
                                                  }}
                                                />
                                              </Show>
                                            </div>
                                            <div class={ui.symActions}>
                                              <button
                                                class={ui.symActionBtn}
                                                title={
                                                  transform().visible
                                                    ? 'Hide'
                                                    : 'Show'
                                                }
                                                onClick={() => {
                                                  setFlameDescriptor(
                                                    (draft) => {
                                                      draft.transforms[
                                                        tid
                                                      ]!.visible =
                                                        !draft.transforms[tid]!
                                                          .visible
                                                    },
                                                  )
                                                }}
                                              >
                                                {transform().visible ? (
                                                  <Eye />
                                                ) : (
                                                  <EyeOff />
                                                )}
                                              </button>
                                              <button
                                                class={ui.symActionBtn}
                                                title="Remove"
                                                onClick={() => {
                                                  setFlameDescriptor(
                                                    (draft) => {
                                                      delete draft.transforms[
                                                        tid
                                                      ]
                                                    },
                                                  )
                                                }}
                                              >
                                                <Cross />
                                              </button>
                                            </div>
                                          </div>
                                        )
                                      }}
                                    </For>
                                  </div>
                                </div>
                              </CollapsibleCard>
                            </Show>
                            <Card class={ui.buttonCard}>
                              <button
                                class={ui.addFlameButton}
                                onClick={() => {
                                  setFlameDescriptor((draft) => {
                                    draft.transforms[generateTransformId()] =
                                      deepClone(newDefaultTransform())
                                  })
                                }}
                              >
                                New transform
                              </button>
                              <button
                                class={ui.addFlameButton}
                                data-tour-target="add-symmetry"
                                disabled={symTransforms().length > 0}
                                title={
                                  symTransforms().length > 0
                                    ? 'Symmetry already applied'
                                    : 'Add 3-fold rotational symmetry'
                                }
                                onClick={() => {
                                  applySymmetry(3, 'rotational')
                                }}
                              >
                                Add symmetry
                              </button>
                              <button
                                class={ui.addFlameButton}
                                onClick={() => {
                                  void showMigrationModal(
                                    structuredClone(
                                      JSON.parse(
                                        JSON.stringify(flameDescriptor),
                                      ),
                                    ),
                                  )
                                }}
                              >
                                Migration
                              </button>
                            </Card>
                            <CollapsibleCard title="Render">
                              <Card>
                                {/* -- Tone Mapping -- */}
                                <div class={ui.settingsGroup}>
                                  <span class={ui.settingsGroupLabel}>
                                    Tone Mapping
                                  </span>
                                  <div
                                    class={ui.parameterTarget}
                                    onClick={() => {
                                      setTargetedParameter('skipIters')
                                    }}
                                  >
                                    <Slider
                                      label="Skip Iterations"
                                      value={
                                        flameDescriptor.renderSettings.skipIters
                                      }
                                      min={0}
                                      max={30}
                                      step={1}
                                      onInput={(newSkipIters) => {
                                        setFlameDescriptor((draft) => {
                                          draft.renderSettings.skipIters =
                                            newSkipIters
                                        })
                                      }}
                                      formatValue={(value) => value.toString()}
                                      dataParameterPath="skipIters"
                                      data-tour-target="skipIters-slider"
                                    />
                                  </div>
                                  <div
                                    class={ui.parameterTarget}
                                    onClick={() => {
                                      setTargetedParameter('plotsPerChain')
                                    }}
                                  >
                                    <Slider
                                      label="Point Batch"
                                      data-tour-target="pointBatch-slider"
                                      value={
                                        flameDescriptor.renderSettings
                                          .plotsPerChain
                                      }
                                      min={1}
                                      max={32}
                                      step={1}
                                      onInput={(plotsPerChain) => {
                                        setFlameDescriptor((draft) => {
                                          draft.renderSettings.plotsPerChain =
                                            plotsPerChain
                                        })
                                      }}
                                      formatValue={(value) => value.toString()}
                                      dataParameterPath="plotsPerChain"
                                    />
                                  </div>
                                  <div
                                    class={ui.parameterTarget}
                                    onClick={() => {
                                      setTargetedParameter('exposure')
                                    }}
                                  >
                                    <Slider
                                      label="Exposure"
                                      value={
                                        flameDescriptor.renderSettings.exposure
                                      }
                                      min={-8}
                                      max={8}
                                      step={0.001}
                                      onInput={(newExp) => {
                                        setFlameDescriptor((draft) => {
                                          draft.renderSettings.exposure = newExp
                                          // With auto-exposure on, a manual change
                                          // re-bases it: this value becomes the
                                          // baseline at the current zoom, and zoom
                                          // scales from here.
                                          if (
                                            draft.renderSettings
                                              .autoExposure3D &&
                                            (draft.renderSettings.dimensions ??
                                              2) === 3
                                          ) {
                                            draft.renderSettings.autoExposure3DBase =
                                              newExp
                                            draft.renderSettings.autoExposure3DRefRadius =
                                              draft.renderSettings.camera3D
                                                ?.radius ?? 5
                                          }
                                        })
                                      }}
                                      formatValue={(value) => value.toFixed(2)}
                                      dataParameterPath="exposure"
                                      data-tour-target="exposure-slider"
                                    />
                                  </div>
                                  <Show
                                    when={
                                      flameDescriptor.renderSettings
                                        .dimensions === 3
                                    }
                                  >
                                    <label
                                      style={{
                                        // Span both grid columns so the checkbox row
                                        // doesn't consume a value-column cell and
                                        // shift the slider rows out of alignment.
                                        'grid-column': '1 / -1',
                                        display: 'flex',
                                        'align-items': 'center',
                                        gap: '6px',
                                        'font-size': '12px',
                                        cursor: 'pointer',
                                        padding: '2px 4px',
                                      }}
                                    >
                                      <Checkbox
                                        checked={
                                          flameDescriptor.renderSettings
                                            .autoExposure3D
                                        }
                                        onChange={(checked) => {
                                          setFlameDescriptor((draft) => {
                                            draft.renderSettings.autoExposure3D =
                                              checked
                                            if (checked) {
                                              draft.renderSettings.autoExposure3DRefRadius =
                                                draft.renderSettings.camera3D
                                                  ?.radius ?? 5
                                              draft.renderSettings.autoExposure3DBase =
                                                draft.renderSettings.exposure
                                            }
                                          })
                                        }}
                                      />
                                      <span>Auto exposure on zoom</span>
                                    </label>
                                    <Show
                                      when={
                                        flameDescriptor.renderSettings
                                          .autoExposure3D
                                      }
                                    >
                                      <div class={ui.parameterTarget}>
                                        <Slider
                                          label="Auto Strength"
                                          value={
                                            flameDescriptor.renderSettings
                                              .autoExposure3DStrength
                                          }
                                          min={0}
                                          max={3}
                                          step={0.05}
                                          onInput={(strength) => {
                                            setFlameDescriptor((draft) => {
                                              draft.renderSettings.autoExposure3DStrength =
                                                strength
                                            })
                                          }}
                                          formatValue={(value) =>
                                            value.toFixed(2)
                                          }
                                        />
                                      </div>
                                    </Show>
                                  </Show>
                                  <div
                                    class={ui.parameterTarget}
                                    onClick={() => {
                                      setTargetedParameter('gamma')
                                    }}
                                  >
                                    <Slider
                                      label="Gamma"
                                      value={
                                        flameDescriptor.renderSettings.gamma
                                      }
                                      min={0.1}
                                      max={8}
                                      step={0.01}
                                      onInput={(newVal) => {
                                        setFlameDescriptor((draft) => {
                                          draft.renderSettings.gamma = newVal
                                        })
                                      }}
                                      formatValue={(value) => value.toFixed(2)}
                                      dataParameterPath="gamma"
                                      data-tour-target="gamma-slider"
                                    />
                                  </div>
                                  <div
                                    class={ui.parameterTarget}
                                    onClick={() => {
                                      setTargetedParameter('contrast')
                                    }}
                                  >
                                    <Slider
                                      label="Contrast"
                                      value={
                                        flameDescriptor.renderSettings.contrast
                                      }
                                      min={0.01}
                                      max={20}
                                      step={0.01}
                                      onInput={(newVal) => {
                                        setFlameDescriptor((draft) => {
                                          draft.renderSettings.contrast = newVal
                                        })
                                      }}
                                      formatValue={(value) => value.toFixed(2)}
                                      dataParameterPath="contrast"
                                      data-tour-target="contrast-slider"
                                    />
                                  </div>
                                  <div
                                    class={ui.parameterTarget}
                                    onClick={() => {
                                      setTargetedParameter('vibrancy')
                                    }}
                                  >
                                    <Slider
                                      label="Vibrancy"
                                      value={
                                        flameDescriptor.renderSettings.vibrancy
                                      }
                                      min={0}
                                      max={3}
                                      step={0.05}
                                      onInput={(newVibrancy) => {
                                        setFlameDescriptor((draft) => {
                                          draft.renderSettings.vibrancy =
                                            newVibrancy
                                        })
                                      }}
                                      formatValue={(value) => value.toFixed(2)}
                                      dataParameterPath="vibrancy"
                                      data-tour-target="vibrancy-slider"
                                    />
                                  </div>
                                  <div
                                    class={ui.parameterTarget}
                                    onClick={() => {
                                      setTargetedParameter('highlightPower')
                                    }}
                                  >
                                    <Slider
                                      label="Highlight Power"
                                      value={
                                        flameDescriptor.renderSettings
                                          .highlightPower
                                      }
                                      min={0}
                                      max={2}
                                      step={0.01}
                                      onInput={(newVal) => {
                                        setFlameDescriptor((draft) => {
                                          draft.renderSettings.highlightPower =
                                            newVal
                                        })
                                      }}
                                      formatValue={(value) => value.toFixed(2)}
                                      dataParameterPath="highlightPower"
                                      data-tour-target="highlightPower-slider"
                                    />
                                  </div>
                                  <Show
                                    when={
                                      (flameDescriptor.renderSettings
                                        .dimensions ?? 2) === 3
                                    }
                                  >
                                    <div
                                      class={ui.parameterTarget}
                                      onClick={() => {
                                        setTargetedParameter('depthColorPower')
                                      }}
                                    >
                                      <Slider
                                        label="Depth Coloring"
                                        value={
                                          flameDescriptor.renderSettings
                                            .depthColorPower ?? 0.0
                                        }
                                        min={0}
                                        max={5}
                                        step={0.05}
                                        onInput={(newVal) => {
                                          setFlameDescriptor((draft) => {
                                            draft.renderSettings.depthColorPower =
                                              newVal
                                          })
                                        }}
                                        formatValue={(value) =>
                                          value.toFixed(2)
                                        }
                                        dataParameterPath="depthColorPower"
                                        data-tour-target="depthColorPower-slider"
                                      />
                                    </div>
                                    <div
                                      class={ui.parameterTarget}
                                      onClick={() => {
                                        setTargetedParameter('lightPower')
                                      }}
                                    >
                                      <Slider
                                        label="Light Power"
                                        value={
                                          flameDescriptor.renderSettings
                                            .lightPower ?? 0.0
                                        }
                                        min={0}
                                        max={1.5}
                                        step={0.01}
                                        onInput={(newVal) => {
                                          setFlameDescriptor((draft) => {
                                            draft.renderSettings.lightPower =
                                              newVal
                                          })
                                        }}
                                        formatValue={(value) =>
                                          value.toFixed(2)
                                        }
                                        dataParameterPath="lightPower"
                                        data-tour-target="lightPower-slider"
                                      />
                                    </div>
                                  </Show>
                                  <div
                                    class={ui.parameterTarget}
                                    onClick={() => {
                                      setTargetedParameter(
                                        'densityEstimationQuality',
                                      )
                                    }}
                                  >
                                    <Slider
                                      label="Filter Quality"
                                      value={
                                        flameDescriptor.renderSettings
                                          .densityEstimationQuality ?? 0.8
                                      }
                                      min={0}
                                      max={1}
                                      step={0.01}
                                      onInput={(newVal) => {
                                        setFlameDescriptor((draft) => {
                                          draft.renderSettings.densityEstimationQuality =
                                            newVal
                                        })
                                      }}
                                      formatValue={(value) => value.toFixed(2)}
                                      dataParameterPath="densityEstimationQuality"
                                      data-tour-target="filterQuality-slider"
                                    />
                                  </div>
                                  <div
                                    class={ui.parameterTarget}
                                    onClick={() => {
                                      setTargetedParameter('estimatorCurve')
                                    }}
                                  >
                                    <Slider
                                      label="Estimator Curve"
                                      value={
                                        flameDescriptor.renderSettings
                                          .estimatorCurve ?? 0.5
                                      }
                                      min={0.1}
                                      max={1}
                                      step={0.05}
                                      onInput={(newVal) => {
                                        setFlameDescriptor((draft) => {
                                          draft.renderSettings.estimatorCurve =
                                            newVal
                                        })
                                      }}
                                      formatValue={(value) => value.toFixed(2)}
                                      dataParameterPath="estimatorCurve"
                                      data-tour-target="estimatorCurve-slider"
                                      disabled={stochasticFilterEnabled()}
                                      disabledReason="The estimator curve only affects the density-estimation pass, which is bypassed while the Mitchell-Netravali (MN) filter is active. Turn off MN to use it."
                                    />
                                  </div>
                                </div>

                                {/* -- Modes -- */}
                                <div class={ui.settingsGroup}>
                                  <span class={ui.settingsGroupLabel}>
                                    Modes
                                  </span>
                                  <div
                                    class={ui.parameterTarget}
                                    onClick={() => {
                                      setTargetedParameter('drawMode')
                                    }}
                                  >
                                    <label
                                      class={ui.labeledInput}
                                      data-tour-target="drawMode-select"
                                    >
                                      <span>
                                        <KeyframeDiamond parameterPath="drawMode" />
                                        Draw Mode
                                      </span>
                                      <select
                                        class={ui.select}
                                        value={
                                          flameDescriptor.renderSettings
                                            .drawMode
                                        }
                                        onChange={(ev) => {
                                          const mode = ev.currentTarget.value
                                          const update = () => {
                                            setFlameDescriptor((draft) => {
                                              draft.renderSettings.drawMode =
                                                mode as 'light' | 'paint'
                                            })
                                          }
                                          if (
                                            'startViewTransition' in document
                                          ) {
                                            document.startViewTransition(update)
                                          } else {
                                            update()
                                          }
                                        }}
                                      >
                                        <For
                                          each={recordKeys(drawModeToImplFn)}
                                        >
                                          {(drawMode) => (
                                            <option value={drawMode}>
                                              {drawMode}
                                            </option>
                                          )}
                                        </For>
                                      </select>
                                      <span></span>
                                    </label>
                                  </div>
                                  <div
                                    class={ui.parameterTarget}
                                    onClick={() => {
                                      setTargetedParameter('colorInitMode')
                                    }}
                                  >
                                    <label
                                      class={ui.labeledInput}
                                      data-tour-target="colorInitMode-select"
                                    >
                                      <span>
                                        <KeyframeDiamond parameterPath="colorInitMode" />
                                        Color Init Mode
                                      </span>
                                      <select
                                        class={ui.select}
                                        value={
                                          flameDescriptor.renderSettings
                                            .colorInitMode
                                        }
                                        onChange={(ev) => {
                                          const mode = ev.currentTarget.value
                                          const update = () => {
                                            setFlameDescriptor((draft) => {
                                              draft.renderSettings.colorInitMode =
                                                mode as
                                                  | 'colorInitZero'
                                                  | 'colorInitPosition'
                                            })
                                          }
                                          if (
                                            'startViewTransition' in document
                                          ) {
                                            document.startViewTransition(update)
                                          } else {
                                            update()
                                          }
                                        }}
                                      >
                                        <For
                                          each={recordKeys(
                                            colorInitModeToImplFn,
                                          )}
                                        >
                                          {(colorInitMode) => (
                                            <option value={colorInitMode}>
                                              {colorInitMode}
                                            </option>
                                          )}
                                        </For>
                                      </select>
                                      <span></span>
                                    </label>
                                  </div>
                                  <div
                                    class={ui.parameterTarget}
                                    onClick={() => {
                                      setTargetedParameter('pointInitMode')
                                    }}
                                  >
                                    <label
                                      class={ui.labeledInput}
                                      data-tour-target="pointInitMode-select"
                                    >
                                      <span>
                                        <KeyframeDiamond parameterPath="pointInitMode" />
                                        Point Init
                                      </span>
                                      <select
                                        class={ui.select}
                                        value={
                                          flameDescriptor.renderSettings
                                            .pointInitMode
                                        }
                                        onChange={(ev) => {
                                          const mode = ev.currentTarget.value
                                          const update = () => {
                                            setFlameDescriptor((draft) => {
                                              draft.renderSettings.pointInitMode =
                                                mode as PointInitMode
                                            })
                                          }
                                          if (
                                            'startViewTransition' in document
                                          ) {
                                            document.startViewTransition(update)
                                          } else {
                                            update()
                                          }
                                        }}
                                      >
                                        <For
                                          each={recordKeys(
                                            (flameDescriptor.renderSettings
                                              .dimensions ?? 2) === 3
                                              ? pointInitMode3DToImplFn
                                              : pointInitModeToImplFn,
                                          )}
                                        >
                                          {(pointInitMode) => (
                                            <option value={pointInitMode}>
                                              {pointInitMode}
                                            </option>
                                          )}
                                        </For>
                                      </select>
                                      <span></span>
                                    </label>
                                  </div>
                                  <div
                                    class={ui.parameterTarget}
                                    onClick={() => {
                                      setTargetedParameter('backgroundColor')
                                    }}
                                  >
                                    <label
                                      class={ui.labeledInput}
                                      data-tour-target="backgroundColor-picker"
                                    >
                                      <span>
                                        <KeyframeDiamond parameterPath="backgroundColor" />
                                        Background Color
                                      </span>
                                      <ColorPicker
                                        value={
                                          flameDescriptor.renderSettings
                                            .backgroundColor
                                            ? vec3f(
                                                ...flameDescriptor
                                                  .renderSettings
                                                  .backgroundColor,
                                              )
                                            : undefined
                                        }
                                        setValue={(newBgColor) => {
                                          setFlameDescriptor((draft) => {
                                            draft.renderSettings.backgroundColor =
                                              newBgColor
                                          })
                                        }}
                                      />
                                    </label>
                                  </div>
                                  <Show
                                    when={
                                      flameDescriptor.renderSettings
                                        .backgroundColor !== undefined
                                    }
                                    fallback={<span class={ui.noSelect} />}
                                  >
                                    <Button
                                      onClick={() => {
                                        setFlameDescriptor((draft) => {
                                          delete draft.renderSettings
                                            .backgroundColor
                                        })
                                      }}
                                    >
                                      Auto
                                    </Button>
                                  </Show>
                                </div>

                                {/* -- Palette -- */}
                                <div
                                  style={{ 'grid-column': '1 / -1' }}
                                  title={
                                    selectedPaletteId() === ''
                                      ? 'Select a palette in the gallery to enable these options'
                                      : undefined
                                  }
                                >
                                  <div
                                    class={ui.settingsGroup}
                                    style={{
                                      opacity:
                                        selectedPaletteId() !== '' ? 1 : 0.4,
                                      'pointer-events':
                                        selectedPaletteId() !== ''
                                          ? 'auto'
                                          : 'none',
                                    }}
                                  >
                                    <span class={ui.settingsGroupLabel}>
                                      Palette
                                    </span>
                                    <div
                                      class={ui.parameterTarget}
                                      onClick={() => {
                                        setTargetedParameter('paletteSpeed')
                                      }}
                                    >
                                      <Slider
                                        label="Palette Speed"
                                        value={
                                          flameDescriptor.renderSettings
                                            .paletteSpeed
                                        }
                                        min={0}
                                        max={10}
                                        step={0.1}
                                        onInput={(newVal) => {
                                          setFlameDescriptor((draft) => {
                                            draft.renderSettings.paletteSpeed =
                                              newVal
                                          })
                                        }}
                                        formatValue={(value) =>
                                          value.toFixed(1)
                                        }
                                        dataParameterPath="paletteSpeed"
                                        data-tour-target="paletteSpeed-slider"
                                      />
                                    </div>
                                    <div
                                      class={ui.parameterTarget}
                                      onClick={() => {
                                        setTargetedParameter('paletteMode')
                                      }}
                                    >
                                      <label
                                        class={ui.labeledInput}
                                        data-tour-target="paletteMode-select"
                                      >
                                        <span>Palette Mode</span>
                                        <select
                                          class={ui.select}
                                          value={
                                            flameDescriptor.renderSettings
                                              .paletteMode ?? 0
                                          }
                                          onChange={(ev) => {
                                            const mode = parseInt(
                                              ev.currentTarget.value,
                                            ) as 0 | 1
                                            setFlameDescriptor((draft) => {
                                              draft.renderSettings.paletteMode =
                                                mode
                                            })
                                          }}
                                        >
                                          <option value={0}>
                                            Density Shift
                                          </option>
                                          <option value={1}>
                                            Hue Rotation (flam3)
                                          </option>
                                        </select>
                                        <span></span>
                                      </label>
                                    </div>
                                    <div
                                      class={ui.parameterTarget}
                                      onClick={() => {
                                        setTargetedParameter('palettePhase')
                                      }}
                                    >
                                      <Slider
                                        label="Palette Phase"
                                        value={
                                          flameDescriptor.renderSettings
                                            .palettePhase
                                        }
                                        min={0}
                                        max={1}
                                        step={0.05}
                                        onInput={(newVal) => {
                                          setFlameDescriptor((draft) => {
                                            draft.renderSettings.palettePhase =
                                              newVal
                                          })
                                        }}
                                        formatValue={(value) =>
                                          value.toFixed(2)
                                        }
                                        dataParameterPath="palettePhase"
                                        data-tour-target="palettePhase-slider"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            </CollapsibleCard>
                            <CollapsibleCard
                              title="Metadata"
                              defaultOpen={false}
                              data-tour-target="metadata-card"
                            >
                              <Card>
                                <div
                                  style={{
                                    display: 'flex',
                                    'flex-direction': 'column',
                                    gap: '0.5rem',
                                    width: '100%',
                                    'grid-column': '1 / -1',
                                  }}
                                >
                                  <div>
                                    <label class={ui.metadataLabel}>Name</label>
                                    <input
                                      class={ui.metadataInput}
                                      type="text"
                                      placeholder="Flame Name"
                                      value={
                                        flameDescriptor.metadata?.name ?? ''
                                      }
                                      onInput={(e) => {
                                        setFlameDescriptor((draft) => {
                                          if (!draft.metadata) {
                                            draft.metadata = {
                                              name: '',
                                              description: '',
                                              author: '',
                                            }
                                          }
                                          draft.metadata.name =
                                            e.currentTarget.value
                                        })
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <label class={ui.metadataLabel}>
                                      Description
                                    </label>
                                    <textarea
                                      class={ui.metadataTextarea}
                                      placeholder="Description"
                                      value={
                                        flameDescriptor.metadata?.description ??
                                        ''
                                      }
                                      onInput={(e) => {
                                        setFlameDescriptor((draft) => {
                                          if (!draft.metadata) {
                                            draft.metadata = {
                                              name: '',
                                              description: '',
                                              author: '',
                                            }
                                          }
                                          draft.metadata.description =
                                            e.currentTarget.value
                                        })
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <label class={ui.metadataLabel}>
                                      Author
                                    </label>
                                    <input
                                      class={ui.metadataInput}
                                      type="text"
                                      placeholder="Author"
                                      value={
                                        flameDescriptor.metadata?.author ?? ''
                                      }
                                      onInput={(e) => {
                                        setFlameDescriptor((draft) => {
                                          if (!draft.metadata) {
                                            draft.metadata = {
                                              name: '',
                                              description: '',
                                              author: '',
                                            }
                                          }
                                          draft.metadata.author =
                                            e.currentTarget.value
                                        })
                                      }}
                                    />
                                  </div>
                                </div>
                              </Card>
                            </CollapsibleCard>
                          </Show>
                        </>
                      }
                    >
                      <Show
                        when={showBlendGallery()}
                        fallback={
                          <Show
                            when={showAudioPanel()}
                            fallback={
                              <SonificationPanel
                                onClose={() => setShowSonificationPanel(false)}
                                enabled={sonificationEnabled}
                                onEnabledChange={setSonificationEnabled}
                                config={sonificationConfig}
                                onConfigChange={setSonificationConfig}
                              />
                            }
                          >
                            <AudioReactivePanel
                              onClose={() => setShowAudioPanel(false)}
                              audioBuffer={audioBuffer}
                              onAudioChange={(buf) => {
                                setAudioBuffer(buf)
                                setFileAnalyzer(undefined)
                                if (!buf) {
                                  setAudioEnabled(false)
                                } else {
                                  setTimeout(async () => {
                                    setFileAnalyzer(
                                      await createAudioAnalyzer(buf, 30),
                                    )
                                  }, 30)
                                }
                                setPlaybackPaused(false)
                                setPlaybackTime(0)
                                setSeekTarget(null)
                              }}
                              audioMapping={audioMapping}
                              onMappingChange={setAudioMapping}
                              audioEnabled={audioEnabled}
                              onEnabledChange={setAudioEnabled}
                              audioSource={audioSource}
                              onSourceChange={setAudioSource}
                              liveAnalyzer={liveAnalyzer}
                              onLiveAnalyzerChange={setLiveAnalyzer}
                              playbackPaused={playbackPaused}
                              onPausedChange={setPlaybackPaused}
                              playbackTime={playbackTime}
                              onSeek={setSeekTarget}
                              fileAnalyzer={fileAnalyzer}
                              transforms={transformInfos()}
                            />
                          </Show>
                        }
                      >
                        <BlendFlameGallery
                          heading={
                            blendIntent() === 'morph'
                              ? 'Pick End Flame'
                              : blendIntent() === 'breed' ||
                                  blendIntent() === 'evolve'
                                ? 'Pick Second Parent'
                                : blendIntent() === 'diff'
                                  ? 'Pick Flame to Compare'
                                  : 'Pick Blend Flame'
                          }
                          onSelect={(flame) => {
                            prevBlendFlame = undefined
                            if (blendIntent() === 'morph') {
                              setupMorph(flame)
                            } else if (blendIntent() === 'breed') {
                              void _requestModal({
                                content: ({ respond }) => (
                                  <BreedGallery
                                    parentA={flameDescriptor}
                                    parentB={deepClone(flame)}
                                    parentInfo={{
                                      nameA:
                                        flameDescriptor.metadata?.name ||
                                        'Current',
                                      nameB: flame.metadata?.name || 'Selected',
                                    }}
                                    hardwareTier={props.hardwareTier}
                                    onApply={(child) => {
                                      if (blendFlame())
                                        showToast(
                                          'Blend is still active — the loaded flame will look mixed',
                                          4000,
                                        )
                                      history.replace(deepClone(child))
                                    }}
                                    onChangeParent={() => {
                                      respond()
                                      pickBreedFlame()
                                    }}
                                    onCompare={openDiffAsModal}
                                    respond={respond}
                                  />
                                ),
                              })
                            } else if (blendIntent() === 'evolve') {
                              void _requestModal({
                                content: ({ respond }) => (
                                  <EvolutionChamber
                                    parentA={flameDescriptor}
                                    parentB={deepClone(flame)}
                                    parentInfo={{
                                      nameA:
                                        flameDescriptor.metadata?.name ||
                                        'Current',
                                      nameB: flame.metadata?.name || 'Selected',
                                    }}
                                    hardwareTier={props.hardwareTier}
                                    onApply={(child) => {
                                      if (blendFlame())
                                        showToast(
                                          'Blend is still active — the loaded flame will look mixed',
                                          4000,
                                        )
                                      history.replace(deepClone(child))
                                    }}
                                    onChangeParent={() => {
                                      respond()
                                      pickEvolveFlame()
                                    }}
                                    onCompare={openDiffAsModal}
                                    respond={respond}
                                  />
                                ),
                              })
                            } else if (blendIntent() === 'diff') {
                              openDiffView(flameDescriptor, flame)
                            } else {
                              setBlendFlame(deepClone(flame))
                            }
                            // Single close for every intent branch above.
                            setShowBlendGallery(false)
                          }}
                          onPreviewBlend={handlePreviewBlend}
                          onPreviewName={(name) => setHoveredBlendName(name)}
                          onClose={() => {
                            handlePreviewBlend(null)
                            setHoveredBlendName(null)
                            setShowBlendGallery(false)
                          }}
                        />
                      </Show>
                    </Show>
                  }
                >
                  {(dv) => (
                    <div class={diffUi.panel}>
                      <div class={diffUi.panelHeader}>
                        <button
                          class={diffUi.backBtn}
                          onClick={closeSidebarDiff}
                        >
                          ← Back to Editor
                        </button>
                      </div>
                      <div class={diffUi.panelScroll}>
                        <DiffViewContent
                          flameA={dv.flameA}
                          flameB={dv.flameB}
                        />
                      </div>
                    </div>
                  )}
                </Show>
              </div>
            </div>
          </Show>
          <FloatingActions
            disabled={animationExportRunning()}
            initialLeft={floatingLeft()}
            initialTop={floatingTop()}
            onNewFlame={() => {
              if (timeline.isPlaying()) timeline.pause()
              // Undo restores the flame, but keyframe tracks aren't part of
              // change history — flush unsaved work (flame + animation) to
              // Recents so a reset can't silently destroy anything. Unlike
              // saveRecentFlame, the upsert never declines on a full list.
              flushDirtyToRecents()
              const is3D =
                (flameDescriptor.renderSettings.dimensions ?? 2) === 3
              const flame = deepClone(is3D ? initExample3D : initExample)
              history.replace(flame, 'New Flame')
              setLoadedAnimation({ flame, tracks: [] })
              showToast('Fresh flame loaded — undo restores the previous one')
            }}
            onLoadFlame={() => {
              if (timeline.isPlaying()) timeline.pause()
              // Loading replaces the flame and resets dirty tracking — flush
              // unsaved work first so it stays recoverable from Recents.
              flushDirtyToRecents()
              void showLoadFlameModal()
            }}
            onSaveForLater={async () => {
              const tracks = timeline.tracks()
              const success = saveRecentFlame(
                flameDescriptor,
                undefined,
                tracks,
                false,
              )
              if (!success) {
                const oldest = getOldestRecentFlame()
                const oldestName = oldest?.name || 'Flame'
                const confirmed = await _requestModal<boolean>({
                  content: ({ respond }) => (
                    <ConfirmOverwriteRecentModal
                      oldestName={oldestName}
                      respond={respond}
                    />
                  ),
                })
                if (confirmed) {
                  saveRecentFlame(flameDescriptor, undefined, tracks, true)
                  markSavedBaseline()
                  showToast(
                    tracks.length > 0
                      ? 'Flame + animation saved (replaced oldest)'
                      : 'Flame saved (replaced oldest)',
                  )
                }
              } else {
                markSavedBaseline()
                showToast(
                  tracks.length > 0
                    ? 'Flame + animation saved for later'
                    : 'Flame saved for later',
                )
              }
            }}
            onRender={() => {
              if (timeline.isPlaying()) timeline.pause()

              void showExportPngDialog()
            }}
            onQuickExport={quickExport}
            onShareLink={() => {
              if (timeline.isPlaying()) timeline.pause()

              void showShareLinkModal()
            }}
            onShareDiscord={shareToDiscord}
            onLogoFavicon={showLogoFaviconGenerator}
            onRandomizeColors={() => {
              setFlameDescriptor((draft) => {
                draft.transforms = randomizeAllColors(draft.transforms)
              })
            }}
            hideDiceButtons={hideDiceButtons}
            setHideDiceButtons={setHideDiceButtons}
            animationEnabled={animationEnabled}
            setAnimationEnabled={(v) => {
              if (IS_DEV) console.info('[anim] floating toggle →', v)
              setAnimationEnabled(v)
            }}
            showTimeline={showTimeline}
            setShowTimeline={setShowTimeline}
            adaptiveFilterEnabled={adaptiveFilterEnabled}
            setAdaptiveFilterEnabled={setAdaptiveFilterEnabled}
            stochasticFilterEnabled={stochasticFilterEnabled}
            setStochasticFilterEnabled={setStochasticFilterEnabled}
            isPlaying={() => timeline.isPlaying()}
            togglePlay={() => {
              if (!animationEnabled()) {
                setAnimationEnabled(true)
              }
              timeline.togglePlay()
            }}
            qualityPreset={qualityPreset}
            setQualityPreset={(key) => {
              if (IS_DEV) {
                console.info(
                  '[App] setQualityPreset (floating)',
                  `key=${key}`,
                  `current=${qualityPreset()}`,
                )
              }
              setQualityPreset(key as QualityPreset)
            }}
            accumulatedPointCount={accumulatedPointCount}
            qualityPointCountLimit={qualityPointCountLimit()}
            dimensions={() => flameDescriptor.renderSettings.dimensions ?? 2}
            setDimensions={(v) => {
              const current = flameDescriptor.renderSettings.dimensions ?? 2
              if (v === current) return
              // The stash below is in-memory only — flush unsaved work to
              // Recents first so switch-then-close can't lose it.
              flushDirtyToRecents()
              // Stash the active flame AND its animation tracks under the
              // current dimension; restore the target dimension's own pair so
              // 2D and 3D each keep independent animations.
              if (current === 3) {
                stashedFlame3D = deepClone(flameDescriptor)
                stashedTracks3D = deepClone(timeline.tracks())
              } else {
                stashedFlame2D = deepClone(flameDescriptor)
                stashedTracks2D = deepClone(timeline.tracks())
              }
              // Fly mode only makes sense in 3D.
              if (v !== 3) setFlyMode(false)
              const restored =
                v === 3
                  ? (stashedFlame3D ?? example34)
                  : (stashedFlame2D ?? initExample)
              const restoredTracks = v === 3 ? stashedTracks3D : stashedTracks2D
              history.replace(deepClone(restored))
              // Swap the timeline to the target dimension's tracks (empty on
              // first entry — matches the starter flame).
              timeline.loadTracks(restoredTracks ?? [])
              // Mode switches restore stashed/starter state — not an edit.
              markLoadedBaseline()
            }}
            flyMode={flyMode}
            setFlyMode={(v) => {
              setFlyMode(v)
              if (v) {
                showToast(
                  'Fly mode: click to look around · WASD/arrows move · Space/C up/down · Q/E roll · Esc to release',
                )
              }
            }}
            sidebarOpen={showSidebar}
            onToggleSidebar={() => {
              // Same as the 'F' shortcut, so it works without a keyboard.
              if ('startViewTransition' in document) {
                document.startViewTransition(() => {
                  setShowSidebar((p) => !p)
                })
              } else {
                setShowSidebar((p) => !p)
              }
            }}
          />
          <SpotlightTour tourContext={tourContext} />
          <SoftwareVersion
            showBenchmark={() => {
              void showBenchmark()
            }}
            showDocs={() => {
              void showDocumentation()
            }}
            showHelp={createShowHelp(
              quickPickerMode,
              setQuickPickerMode,
              sidebarLayoutMode,
              setSidebarLayoutMode,
              isCompact,
              setCompact,
              theme,
              setTheme,
              IS_DEV ? () => setDevCrashTest(true) : undefined,
              () => props.hardwareTier ?? null,
              props.onHardwareTierChange,
            )}
          />
          <Show when={devCrashTest()}>
            {(() => {
              throw new Error('[DEV] Injected crash from About panel')
            })()}
          </Show>
        </Dropzone>
      </TimelineContextProvider>
    </ChangeHistoryContextProvider>
  )
}
